import { clinicalLimitStatus, formatStructuredClinicalLimit } from '@/clients/details/v2/components/analysis/clinicalTargetSemantics';
import { suggestedRdaToneStyles } from '@/clients/details/v2/components/analysis/suggestedRdaTone';
import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ClinicalFinalizeIssuesPanel from './ClinicalFinalizeIssuesPanel';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  DragOverlay,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  addRecipesToManualMealPlan,
  addRecipesToGenericMealPlan,
  createMealSlot,
  createGenericMealSlot,
  deleteMealSlot,
  deleteGenericMealSlot,
  finalizeManualMealPlan,
  validateManualMealPlanFinalization,
  removeManualMealPlanItem,
  removeGenericMealPlanItem,
  removeMealPlanTemplateItem,
  reorderManualMealSlots,
  searchMealPlanItems,
  importMealPlanTemplate,
  importExistingGenericMealPlan,
  deleteMealPlanTemplate,
  saveDraftMealPlanAsTemplate,
  saveDraftMealSlotAsTemplate,
  saveFinalizedMealPlanAsTemplate,
  saveFinalizedMealSlotAsTemplate,
  getMealPlanNutritionalAnalysis,
  updateMealSlot,
  updateFinalizedMealPlanItem,
  updateGenericMealSlot,
  updateGenericMealPlanItem,
  updateMealPlanNotes,
  type GenericMealPlanSummary,
  type MealPlanTemplateSummary,
  type SaveMealSlotTemplateRequest,
  type UpdateMealPlanNotesRequest,
  type ManualFinalizeValidation,
  type ManualFinalizeWarningIssue,
} from '@/lib/api/mealPlans';
import { MealPlanNotesControl } from '@/components/meal-planning/MealPlanNotesControl';
import { getCurrentUser } from '@/lib/api/auth';
import { getDrvPrimaryTypes } from '@/lib/api/drv';
import { getClientById } from '@/lib/apiService';
import {
  trackMealPlanAnalyseClicked,
  trackMealPlanDetailedReportClicked,
  trackMealPlanFinalizeClicked,
  trackMealPlanTemplateImportClicked,
  trackMealPlanTemplateSaveClicked,
} from '@/lib/analytics/trackers/mealPlanActions';
import { useAuth } from '@/hooks/useAuth';
import { useSpoonacularTagEnrichment } from '@/hooks/useSpoonacularTagEnrichment';
import {
  downloadMealPlanPdfFromExportApi,
  downloadGenericMealPlanPdfFromExportApi,
  downloadMealPlanTemplatePdfFromExportApi,
  downloadDraftRecipeDetailsPdfFromRecipeApi,
  downloadRecipeDetailsPdfFromExportApi,
  downloadTemplateRecipeDetailsPdfFromExportApi,
} from '@/utils/meal-plan-ingredients-pdf';
import type { RecipeLibraryItem } from '@/components/shared/ImportFromRecipeLibraryModal';
import type { SaveAsTemplatePayload } from '@/components/shared/SaveAsTemplateModal';
import type { Client } from '@/types/client';
import type { DeleteMealSlotOptions, MealPlanClinicalInsights } from '@/types/meal-plan';
import { getValidCuisineTypes } from '@/lib/clientPreferenceOptions';
import { formatDisplayLabel } from '@/utils/display-labels';
import { extractErrorMessage, showErrorToast, showNotesSavedToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { countClinicalIssueGroups } from '@/utils/clinical-finalize-warnings';
import type { ContributorRow } from '@/components/meal-planning/manual/ManualMealPlanAnalysisDashboard';
import NutritionalAnalysisTopBar from '@/components/meal-planning/manual/NutritionalAnalysisTopBar';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import NutritionalAnalysisSidebar from '@/components/meal-planning/manual/NutritionalAnalysisSidebar';
import NutritionalAnalysisMealCard from './NutritionalAnalysisMealCard';
import NutritionalAnalysisActionsBar from './NutritionalAnalysisActionsBar';
import NutritionalAnalysisOverlays from './NutritionalAnalysisOverlays';
import {
  runAIDraftFoodSelection,
  runAIDraftIngredientSelection,
  runAIDraftQuantitySync,
} from './nutritionalAnalysisAiFlow';
import {
  runManualOrFinalizedFoodSelection,
  runManualOrFinalizedIngredientSelection,
  runManualOrFinalizedQuantitySync,
  type GenericOptionAddTarget,
} from './nutritionalAnalysisManualFlow';
import {
  fetchNutritionalAnalysisDayData,
  sumExpandedFoodRowFatBreakdown,
  type FatBreakdownTotals,
} from './nutritionalAnalysisDataLoader';
import type {
  AddItemSearchFilters,
  ExpandedFoodRow,
  FoodSearchResult,
  GenericMealOptionAddContext,
  MacroTotals,
  MealMeta,
  QuantitySyncField,
  RowSelectionContext,
  SelectedRecipeDetailsContext,
} from './nutritionalAnalysisTypes';
import {
  defaultAddIngredientServingId,
  defaultAddRecipeServingId,
  defaultAIDraftIngredientServingId,
  defaultMacroTargets,
  fallbackMealMeta,
  fallbackRowsByMeal,
  quantitySyncDelayMs,
} from './nutritionalAnalysisConstants';
import { getNutritionTrafficLightColor } from '@/utils/nutrition-traffic-light';
import type { ManualNutritionDrvContext } from './nutritionalAnalysisPure';
import {
  buildAddingItemRowKey,
  buildManualNutritionAnalysisSnapshot,
  clampPercent,
  resolveManualNutritionTargets,
  ensureDefaultEmptyRowsForMeal,
  formatCalories,
  formatNumber,
  isIngredientRow,
  mapFoodSearchResults,
  mealDisplayTimeToApiHHMM,
  normalizeItemType,
  numberFromUnknown,
  nutritionValue,
  parseRecipeServingId,
  parseRecipeServingSizesFromRecord,
  resolveFinalizedMealType,
  resolveInitialRecipeServingId,
  resolveMealDisplayName,
  resolveMealTime,
  resolveEmptyRowSelectionContext,
  resolveRowId,
  rowMacroTotals,
  isPickableNutritionRow,
  toEditableNumber,
  formatMealSlotTimeForDisplay,
} from './nutritionalAnalysisPure';

export interface NutritionalAnalysisWorkspaceProps {
  planId?: string;
  clientId?: string;
  dayNumber?: number;
  onBack?: () => void;
  embedded?: boolean;
  /** Overrides NutritionalAnalysisTopBar back label (e.g. `"Back"` on generic meal plan views). */
  topBarBackLabel?: string;
  planMode?: 'manual' | 'generic';
  showClientSummaryStrip?: boolean;
  clinicalTags?: string[];
  showSaveAsTemplateFlow?: boolean;
  /** Search templates in the actions bar; when omitted on a non-embedded page, defaults to `"Search templates..."`. */
  templatesSearchPlaceholder?: string;
  /** After server-backed changes (e.g. delete), so parent views can refetch the full meal plan. */
  onMealPlanUpdated?: () => void | Promise<void>;
}

const FINALIZED_MEAL_PLAN_STATUSES = new Set(['finalized', 'completed', 'published', 'active']);
const EMPTY_ADD_ITEM_SEARCH_FILTERS: AddItemSearchFilters = { allergies: [] };

const normalizeDietaryLookupKey = (value: string): string => (
  value.trim().toLowerCase().replace(/[\s-]+/g, '_')
);

const CUISINE_TYPE_LOOKUP_KEYS = new Set(
  getValidCuisineTypes().flatMap((cuisineType) => [
    normalizeDietaryLookupKey(cuisineType.value),
    normalizeDietaryLookupKey(cuisineType.name),
  ]),
);

const isCuisineTypeValue = (value: string): boolean => (
  CUISINE_TYPE_LOOKUP_KEYS.has(normalizeDietaryLookupKey(value))
);

const uniquePreferenceValues = (values: Array<string | undefined | null>): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    const key = normalizeDietaryLookupKey(trimmed);
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
};

const formatPreferenceDisplayList = (values: string[]): string => (
  values.map((value) => formatDisplayLabel(value)).filter(Boolean).join(', ')
);

const normalizeSearchFilterValues = (value: unknown): string[] => {
  const entries = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const seen = new Set<string>();
  const normalized: string[] = [];

  entries.forEach((entry) => {
    const raw = String(entry ?? '').trim();
    const key = raw.toLowerCase();
    if (!raw || seen.has(key)) return;
    seen.add(key);
    normalized.push(raw);
  });

  return normalized;
};

const isFinalizedMealPlanStatus = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return FINALIZED_MEAL_PLAN_STATUSES.has(normalized);
};

type DateConflictItem = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
};

function extractDateConflicts(error: unknown): DateConflictItem[] {
  const responseData = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const payload = responseData && typeof responseData === 'object' ? responseData as Record<string, unknown> : {};
  if (payload.code !== 'MEAL_PLAN_DATE_CONFLICT' || !Array.isArray(payload.conflicts)) return [];
  return payload.conflicts
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const rec = item as Record<string, unknown>;
      return {
        id: String(rec.id ?? ''),
        name: String(rec.name ?? 'Meal plan'),
        startDate: typeof rec.startDate === 'string' ? rec.startDate : null,
        endDate: typeof rec.endDate === 'string' ? rec.endDate : null,
      };
    })
    .filter((item): item is DateConflictItem => Boolean(item?.id));
}

const extractMissingMealDayNumber = (error: unknown): number | null => {
  const message = extractErrorMessage(error);
  if (!/has no items|all meals must have at least one recipe or ingredient/i.test(message)) {
    return null;
  }

  const dayMatch = message.match(/\bday\s+(\d+)\b/i);
  if (!dayMatch) {
    return null;
  }

  const dayNumber = Number(dayMatch[1]);
  return Number.isFinite(dayNumber) && dayNumber > 0 ? dayNumber : null;
};

const buildMissingMealDayMessage = (dayNumber: number): string =>
  `Day ${dayNumber} meal slots are incomplete. We are taking you to Day ${dayNumber} so you can add the missing recipes or ingredients.`;

const hasClinicalInsightMealItem = (row: ExpandedFoodRow): boolean =>
  Boolean(
    String(row.item ?? '').trim()
    || String(row.itemId ?? '').trim()
    || String(row.recipeId ?? '').trim()
    || String(row.ingredientId ?? '').trim()
    || String(row.sourceId ?? '').trim()
  );

const pushUniqueClinicalInsightLabel = (labels: string[], seen: Set<string>, value: unknown): void => {
  const text = String(value ?? '').trim();
  if (!text) return;
  const key = text.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  labels.push(text);
};

const resolveClientClinicalInsightLabels = (client: Client | null): string[] => {
  const labels: string[] = [];
  const seen = new Set<string>();
  const record = client as Record<string, unknown> | null;

  const conditionDetails = Array.isArray(record?.medicalConditionDetails)
    ? record.medicalConditionDetails
    : [];
  conditionDetails.forEach((condition) => {
    if (!condition || typeof condition !== 'object') return;
    const conditionRecord = condition as Record<string, unknown>;
    const name = String(conditionRecord.conditionName ?? conditionRecord.conditionId ?? '').trim();
    const subtype = String(conditionRecord.conditionSubtype ?? '').trim();
    if (name && subtype && !name.toLowerCase().includes(subtype.toLowerCase())) {
      pushUniqueClinicalInsightLabel(labels, seen, `${name} - ${subtype}`);
      return;
    }
    pushUniqueClinicalInsightLabel(labels, seen, name || subtype);
  });

  const medicalConditions = Array.isArray(record?.medicalConditions)
    ? record.medicalConditions
    : typeof record?.medicalConditions === 'string'
      ? record.medicalConditions.split(',')
      : [];
  medicalConditions.forEach((condition) => pushUniqueClinicalInsightLabel(labels, seen, condition));

  const pregnancyStatus = String(record?.pregnancyStatus ?? '').trim();
  const pregnancyTrimester = String(record?.pregnancyTrimester ?? '').trim();
  if (pregnancyStatus) {
    pushUniqueClinicalInsightLabel(
      labels,
      seen,
      pregnancyTrimester ? `${pregnancyStatus} - ${pregnancyTrimester}` : pregnancyStatus,
    );
  }

  return labels;
};

function formatConflictDateRange(item: DateConflictItem): string {
  const formatDate = (value: string | null) => {
    if (!value) return '';
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const start = formatDate(item.startDate);
  const end = formatDate(item.endDate);
  return start && end && start !== end ? `${start} - ${end}` : start || end;
}

const resolveSearchResultProvider = (item: FoodSearchResult): 'spoonacular' | 'internal' => {
  if (item.provider === 'spoonacular') return 'spoonacular';
  if (item.provider === 'internal' || item.provider === 'custom') return 'internal';
  return item.source === 'spoonacular' ? 'spoonacular' : 'internal';
};

const resolveSearchResultAddId = (item: FoodSearchResult): string => {
  if (item.type === 'ingredient') {
    return String(item.id ?? '').trim();
  }

  if (item.source === 'cached') {
    return String(item.id ?? '').trim();
  }

  return String(item.id ?? item.sourceId ?? '').trim();
};

const weightUnitKeys = new Set(['g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms', 'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds']);

const resolvePreferredIngredientServingId = (item: FoodSearchResult): string | undefined => {
  if (item.type !== 'ingredient') return undefined;

  const defaultServingId = String(item.defaultServingId ?? '').trim();
  if (defaultServingId) return defaultServingId;

  const servingOverrides = (item.servingOverrides ?? [])
    .map((option) => ({
      servingId: String(option.servingId ?? '').trim(),
      grams: Number(option.grams),
    }))
    .filter((option) => option.servingId && Number.isFinite(option.grams) && option.grams > 0);

  const servingOverride = servingOverrides.find((option) => option.servingId.toLowerCase() === 'serving');
  if (servingOverride) return servingOverride.servingId;

  const semanticOverride = servingOverrides.find((option) => !weightUnitKeys.has(option.servingId.toLowerCase()));
  if (semanticOverride) return semanticOverride.servingId;

  return String(item.possibleUnits?.[0] ?? '').trim() || undefined;
};

const getFoodSearchResultOptionKey = (item: FoodSearchResult): string => {
  const type = String(item.type ?? 'item').trim() || 'item';
  const id = String(item.id ?? '').trim();
  const sourceId = String(item.sourceId ?? '').trim();
  return `${type}:${id}:${sourceId}`;
};

const resolveGenericOptionRowItemId = (row: ExpandedFoodRow): string =>
  String(row.itemId ?? row.ingredientId ?? row.recipeId ?? '').trim();

const buildGenericOptionGroupId = (mealName: string): string => {
  const mealSlug = mealName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'meal';
  return `option-${mealSlug}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
};

type MealSlotDialogMode = 'add' | 'edit';
type TemplateSaveTarget =
  | { kind: 'meal_plan' }
  | { kind: 'meal_slot'; mealName: string };

const extractClientIdFromPath = (path: string): string => {
  const match = path.match(/\/clients\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
};

/** Simplified public template nutrition UI (not for private / own templates). */
function computeIsPublicTemplateViewerLayout(params: {
  isTemplateEditor: boolean;
  isGenericPlan: boolean;
  templateCanEdit: boolean;
  effectiveIsOwn: boolean | null;
  templateVisibility: string | null;
}): boolean {
  const { isTemplateEditor, isGenericPlan, templateCanEdit, effectiveIsOwn, templateVisibility } = params;
  if (!isTemplateEditor || isGenericPlan || templateCanEdit) return false;
  if (effectiveIsOwn === true) return false;
  const v = String(templateVisibility ?? '').toLowerCase().trim();
  if (v === 'private') return false;
  if (effectiveIsOwn === false) return true;
  return v === 'shared_with_practice' || v === 'public';
}

type SortableMealSlotWrapperProps = {
  id: string;
  disabled?: boolean;
  children: (props: {
    setNodeRef: (node: HTMLElement | null) => void;
    attributes: Record<string, unknown>;
    listeners?: Record<string, unknown>;
    style: React.CSSProperties;
    isDragging: boolean;
  }) => React.ReactNode;
};

function SortableMealSlotWrapper({ id, disabled, children }: SortableMealSlotWrapperProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    opacity: isDragging ? 0 : 1,
  };

  return children({
    setNodeRef,
    attributes: attributes as Record<string, unknown>,
    listeners: listeners as Record<string, unknown> | undefined,
    style,
    isDragging,
  });
}

const FIXED_MEAL_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
  { value: 'other', label: 'Others' },
];

const resolveClientDrvSource = (client: Client | null | undefined): string => {
  const drvRequirements = client?.drvRequirements ?? null;
  const source =
    (drvRequirements as { source?: string } | null)?.source ??
    client?.drvSource ??
    'iom_usa';
  return String(source || 'iom_usa').trim() || 'iom_usa';
};

const resolveGenericClientEerKcal = (client: Client | null | undefined): number | undefined => {
  if (!client) return undefined;
  const source = client as Client & { eerCalories?: unknown };
  const candidates = [resolveClientTargetCalories(client), source.eerOverride, source.eerCalories, source.eer];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
};

const buildMealSlotTemplateIdentifier = (
  meal: MealMeta,
  isDraftPlan: boolean
): Pick<SaveMealSlotTemplateRequest, 'mealId' | 'mealOrder' | 'mealName' | 'mealType'> => {
  const mealId = String(meal.mealId ?? '').trim();
  const mealOrder = Number(meal.sortOrder);
  const mealName = String(meal.apiMealName ?? meal.name ?? '').trim();
  const mealType = String(meal.mealType ?? '').trim();

  if (isDraftPlan) {
    if (mealName) return { mealName };
    if (Number.isFinite(mealOrder) && mealOrder > 0) return { mealOrder };
    return {};
  }

  if (mealId) return { mealId };
  if (Number.isFinite(mealOrder) && mealOrder > 0) return { mealOrder };
  if (mealName) return { mealName };
  if (mealType) return { mealType };
  return {};
};

const NutritionalAnalysisWorkspace: React.FC<NutritionalAnalysisWorkspaceProps> = ({
  planId: planIdProp,
  clientId: clientIdProp,
  dayNumber: dayNumberProp,
  onBack,
  embedded = false,
  topBarBackLabel,
  planMode = 'manual',
  showClientSummaryStrip = true,
  clinicalTags,
  showSaveAsTemplateFlow,
  templatesSearchPlaceholder,
  onMealPlanUpdated,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams<{ planId: string; clientId: string; dayNumber: string }>();
  const resolvedPlanId = planIdProp ?? routeParams.planId;
  const routeClientId = String(clientIdProp ?? routeParams.clientId ?? '').trim();
  const fromPath = searchParams.get('from')?.trim() || '';
  const isGenericPlan = planMode === 'generic' || searchParams.get('generic') === 'true';
  const isAssignedGenericUiRoute = searchParams.get('genericAssigned') === 'true';
  const isGenericRouteContext = isGenericPlan || isAssignedGenericUiRoute;
  const clientIdFromReturnPath = extractClientIdFromPath(fromPath);
  const resolvedClientId =
    isGenericPlan && routeClientId === 'template-preview' && clientIdFromReturnPath
      ? clientIdFromReturnPath
      : routeClientId;
  const isTemplateEditor = routeClientId === 'template-preview' || searchParams.get('template') === 'true';
  const shouldUseClientContext =
    Boolean(resolvedClientId) && resolvedClientId !== 'template-preview' && (!isTemplateEditor || isGenericRouteContext);
  const safeFromPath = fromPath.startsWith('/') ? fromPath : '';
  const effectiveTemplatesSearchPlaceholder =
    isTemplateEditor || isGenericRouteContext
      ? undefined
      : (templatesSearchPlaceholder ?? (!embedded ? 'Import from Templates' : undefined));
  const { user } = useAuth();
  const currentUser = getCurrentUser() as unknown as Record<string, unknown> | null;
  const currentUserIdCandidates = Array.from(new Set([
    currentUser?.id,
    currentUser?.linkedProfileId,
    currentUser?.nutritionistId,
    currentUser?.clientId,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)));
  const routePreferredChoices = React.useMemo(() => {
    const routeState = location.state as {
      preferredChoices?: {
        allergies?: unknown;
        intolerances?: unknown;
        cuisineTypes?: unknown;
        preferences?: unknown;
      };
    } | null;
    const preferredChoices = routeState?.preferredChoices;
    if (!preferredChoices || typeof preferredChoices !== 'object') {
      return {
        allergies: [] as string[],
        intolerances: [] as string[],
        cuisineTypes: [] as string[],
        preferences: [] as string[],
      };
    }
    const toList = (value: unknown): string[] =>
      Array.isArray(value)
        ? value.map((item) => String(item ?? '').trim()).filter(Boolean)
        : [];
    return {
      allergies: toList(preferredChoices.allergies),
      intolerances: toList(preferredChoices.intolerances),
      cuisineTypes: toList(preferredChoices.cuisineTypes),
      preferences: toList(preferredChoices.preferences),
    };
  }, [location.state]);
  const canDeleteTemplateFromRouteState = React.useMemo(() => {
    const routeState = location.state as { canDeleteTemplate?: unknown } | null;
    if (routeState && typeof routeState.canDeleteTemplate === 'boolean') {
      return routeState.canDeleteTemplate;
    }
    return false;
  }, [location.state]);
  const routeCanEditTemplate = React.useMemo(() => {
    const routeState = location.state as { canEditTemplate?: boolean } | null;
    return typeof routeState?.canEditTemplate === 'boolean' ? routeState.canEditTemplate : undefined;
  }, [location.state]);
  const routeTemplateIsOwn = React.useMemo(() => {
    const routeState = location.state as { isOwnTemplate?: boolean } | null;
    return typeof routeState?.isOwnTemplate === 'boolean' ? routeState.isOwnTemplate : undefined;
  }, [location.state]);
  const dayNumberValue = dayNumberProp ?? (Number(routeParams.dayNumber || 0) || 1);
  const [selectedDayNumber, setSelectedDayNumber] = React.useState(dayNumberValue);
  const [availableDayNumbers, setAvailableDayNumbers] = React.useState<number[]>([dayNumberValue]);
  const [dayNamesByNumber, setDayNamesByNumber] = React.useState<Record<number, string>>({});
  const dayLabel = dayNamesByNumber[selectedDayNumber]?.trim() || `Day ${selectedDayNumber}`;
  const { previousDayNumber, nextDayNumber } = React.useMemo(() => {
    const sortedDayNumbers = [...availableDayNumbers].sort((left, right) => left - right);
    const currentIndex = sortedDayNumbers.indexOf(selectedDayNumber);
    if (currentIndex < 0) return { previousDayNumber: null, nextDayNumber: null };
    return {
      previousDayNumber: currentIndex > 0 ? sortedDayNumbers[currentIndex - 1] : null,
      nextDayNumber: currentIndex < sortedDayNumbers.length - 1 ? sortedDayNumbers[currentIndex + 1] : null,
    };
  }, [availableDayNumbers, selectedDayNumber]);
  const previousDayLabel = previousDayNumber != null
    ? (dayNamesByNumber[previousDayNumber]?.trim() || `Day ${previousDayNumber}`)
    : null;
  const nextDayLabel = nextDayNumber != null
    ? (dayNamesByNumber[nextDayNumber]?.trim() || `Day ${nextDayNumber}`)
    : null;
  const [planNotes, setPlanNotes] = React.useState<string | null>(null);
  const [planDescription, setPlanDescription] = React.useState<string | null>(null);
  const [dayNotes, setDayNotes] = React.useState<string | null>(null);
  const [isSavingMealPlanNotes, setIsSavingMealPlanNotes] = React.useState(false);
  const [mealMeta, setMealMeta] = React.useState<MealMeta[]>(() => (isGenericRouteContext ? [] : fallbackMealMeta));
  const [expandedCards, setExpandedCards] = React.useState<Record<string, boolean>>(() => (isGenericRouteContext ? {} : { 'Pre-Breakfast': true }));
  const [rowsByMeal, setRowsByMeal] = React.useState<Record<string, ExpandedFoodRow[]>>(() => (isGenericRouteContext ? {} : fallbackRowsByMeal));
  const rowsByMealRef = React.useRef<Record<string, ExpandedFoodRow[]>>(isGenericRouteContext ? {} : fallbackRowsByMeal);
  const quantitySyncTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const qtyDraftCommitTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const qtyDraftByRowKeyRef = React.useRef<Record<string, string>>({});
  const rowItemSearchTimersRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const rowItemLatestQueryRef = React.useRef<Record<string, string>>({});
  const [rowItemSearchResultsById, setRowItemSearchResultsById] = React.useState<Record<string, FoodSearchResult[]>>({});
  const [rowItemSearchLoadingById, setRowItemSearchLoadingById] = React.useState<Record<string, boolean>>({});
  const [rowItemSearchOpenById, setRowItemSearchOpenById] = React.useState<Record<string, boolean>>({});
  /** Generic meal plans: at most one checked row per meal slot (alternatives). Other modes: independent toggles per row. */
  const [genericSlotCheckboxChoice, setGenericSlotCheckboxChoice] = React.useState<Record<string, number | null>>({});
  const [mealRowCheckboxState, setMealRowCheckboxState] = React.useState<Record<string, Record<number, boolean>>>({});
  const [macroTargets, setMacroTargets] = React.useState(defaultMacroTargets);
  const [mealPlanTargetDailyActive, setMealPlanTargetDailyActive] = React.useState(false);
  const [mealPlanDisplayTargetFields, setMealPlanDisplayTargetFields] = React.useState({
    calories: false,
    protein: false,
    carbs: false,
    fat: false,
  });
  const [apiMealMacroTotalsByMealName, setApiMealMacroTotalsByMealName] = React.useState<
    Partial<Record<string, MacroTotals>>
  >({});
  const [apiDayMacroTotals, setApiDayMacroTotals] = React.useState<MacroTotals | null>(null);
  const [apiTopCalorieContributors, setApiTopCalorieContributors] = React.useState<ContributorRow[]>([]);
  const [apiNutrientTotals, setApiNutrientTotals] = React.useState({
    fiber: 0,
    calcium: 0,
    iron: 0,
    vitaminD: 0,
  });
  const [apiFatBreakdownTotals, setApiFatBreakdownTotals] = React.useState<FatBreakdownTotals>({
    mufa: null,
    pufa: null,
    saturatedFat: null,
    transFat: null,
  });
  const [clinicalInsights, setClinicalInsights] = React.useState<MealPlanClinicalInsights | null>(null);
  const [isClinicalInsightsLoading, setIsClinicalInsightsLoading] = React.useState(false);
  const [clinicalInsightsError, setClinicalInsightsError] = React.useState('');
  const [clinicalInsightsRefreshKey, setClinicalInsightsRefreshKey] = React.useState(0);
  const [appliedIntolerances, setAppliedIntolerances] = React.useState<string[]>([]);
  const [appliedAllergies, setAppliedAllergies] = React.useState<string[]>([]);
  const [appliedCuisineTypes, setAppliedCuisineTypes] = React.useState<string[]>([]);
  const [appliedPreferences, setAppliedPreferences] = React.useState<string[]>([]);
  const [loadedClinicalTags, setLoadedClinicalTags] = React.useState<string[]>([]);
  const [importedTemplateIds, setImportedTemplateIds] = React.useState<string[]>([]);
  const [manualDrvContext, setManualDrvContext] = React.useState<ManualNutritionDrvContext | null>(null);
  const [clientDetails, setClientDetails] = React.useState<Client | null>(null);
  const drvPrimaryTypesSourceRef = React.useRef('');
  const isLoadingDrvPrimaryTypesRef = React.useRef(false);
  const [planName, setPlanName] = React.useState('');
  const [planStatus, setPlanStatus] = React.useState<string>('unknown');
  const [planGenerationType, setPlanGenerationType] = React.useState<string>('unknown');
  const [planGenerationSource, setPlanGenerationSource] = React.useState<string>('unknown');
  const [planGenerationState, setPlanGenerationState] = React.useState<string>('unknown');
  const [loadedTemplateKind, setLoadedTemplateKind] = React.useState<'meal_plan' | 'meal_slot' | null>(null);
  const [loadedTemplateCanEdit, setLoadedTemplateCanEdit] = React.useState(true);
  const [loadedTemplateIsOwn, setLoadedTemplateIsOwn] = React.useState<boolean | null>(null);
  const [loadedTemplateVisibility, setLoadedTemplateVisibility] = React.useState<string | null>(null);
  const [isDayDataLoading, setIsDayDataLoading] = React.useState(true);
  const [dayDataLoadError, setDayDataLoadError] = React.useState('');
  const [dayDataReloadKey, setDayDataReloadKey] = React.useState(0);
  const [isExportingPdf, setIsExportingPdf] = React.useState(false);
  const [downloadingRecipeRowKey, setDownloadingRecipeRowKey] = React.useState<string | null>(null);
  const canExportMealPlanPdf = Boolean(resolvedPlanId);
  const [pendingInlineSaveCount, setPendingInlineSaveCount] = React.useState(0);
  const [isAISaveSimulating, setIsAISaveSimulating] = React.useState(false);
  const [finalizeDateConflicts, setFinalizeDateConflicts] = React.useState<DateConflictItem[]>([]);
  const [manualFinalizeValidation, setManualFinalizeValidation] = React.useState<ManualFinalizeValidation | null>(null);
  const [isValidatingManualFinalize, setIsValidatingManualFinalize] = React.useState(false);
  const [highlightDaySelector, setHighlightDaySelector] = React.useState(false);
  const contentScrollRef = React.useRef<HTMLDivElement | null>(null);
  const daySwitcherSectionRef = React.useRef<HTMLDivElement | null>(null);
  const aiSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const daySelectorHighlightTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = React.useState(false);
  const [addItemMealName, setAddItemMealName] = React.useState<string | null>(null);
  const [addItemAnchorEl, setAddItemAnchorEl] = React.useState<HTMLElement | null>(null);
  const [addItemSearchValue, setAddItemSearchValue] = React.useState('');
  const [addItemSearchResults, setAddItemSearchResults] = React.useState<FoodSearchResult[]>([]);
  const [addItemSearchFilters, setAddItemSearchFilters] =
    React.useState<AddItemSearchFilters>(EMPTY_ADD_ITEM_SEARCH_FILTERS);
  const [isAddItemSearching, setIsAddItemSearching] = React.useState(false);
  const [addingItemByRowKey, setAddingItemByRowKey] = React.useState<Record<string, string>>({});
  const [deletingItemByRowKey, setDeletingItemByRowKey] = React.useState<Record<string, true>>({});
  const [addItemGenericOptionContext, setAddItemGenericOptionContext] =
    React.useState<GenericMealOptionAddContext | null>(null);
  const enrichedAddItemSearchResults = useSpoonacularTagEnrichment(
    addItemSearchResults,
    { enabled: isAddItemModalOpen && !isAddItemSearching },
  );
  const [activeRowSelectionContext, setActiveRowSelectionContext] = React.useState<RowSelectionContext>(null);
  const addItemSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isImportRecipesOpen, setIsImportRecipesOpen] = React.useState(false);
  const [importRecipesMealName, setImportRecipesMealName] = React.useState<string | null>(null);
  const [importRecipesOptionContext, setImportRecipesOptionContext] =
    React.useState<GenericMealOptionAddContext | null>(null);
  const [importingRecipesMealName, setImportingRecipesMealName] = React.useState<string | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = React.useState(false);
  const [deleteTemplateConfirmOpen, setDeleteTemplateConfirmOpen] = React.useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = React.useState(false);
  const [templateSaveTarget, setTemplateSaveTarget] = React.useState<TemplateSaveTarget | null>(null);
  const [isAnalysisVisible, setIsAnalysisVisible] = React.useState(true);
  const showAnalysisSidebar = isAnalysisVisible && (!isTemplateEditor || isGenericRouteContext);
  const [draggingMealName, setDraggingMealName] = React.useState<string | null>(null);
  const [isReorderingMealSlots, setIsReorderingMealSlots] = React.useState(false);
  const [mealSlotDeletingName, setMealSlotDeletingName] = React.useState<string | null>(null);
  const isGenericOriginPlan = React.useMemo(() => {
    if (isGenericRouteContext) return true;
    const generationSource = String(planGenerationSource ?? '').trim().toLowerCase();
    const generationType = String(planGenerationType ?? '').trim().toLowerCase();
    return (
      generationSource === 'generic'
      || generationSource === 'generic_assigned'
      || generationType === 'generic'
      || generationType === 'generic_assigned'
    );
  }, [isGenericRouteContext, planGenerationSource, planGenerationType]);
  const [addingGenericOptionMealName, setAddingGenericOptionMealName] = React.useState<string | null>(null);
  const [deleteMealSlotConfirmOpen, setDeleteMealSlotConfirmOpen] = React.useState(false);
  const [pendingDeleteMealSlotName, setPendingDeleteMealSlotName] = React.useState<string | null>(null);
  const [pendingDeleteMealSlot, setPendingDeleteMealSlot] = React.useState<MealMeta | null>(null);
  const mealSlotDeleteInFlightRef = React.useRef(false);
  const modalReopenBlockedUntilRef = React.useRef(0);
  const [editMealDialogOpen, setEditMealDialogOpen] = React.useState(false);
  const [editMealName, setEditMealName] = React.useState('');
  const [editMealOriginalName, setEditMealOriginalName] = React.useState('');
  const [editMealOriginalTime, setEditMealOriginalTime] = React.useState('');
  const [editMealType, setEditMealType] = React.useState('');
  const [editMealTime, setEditMealTime] = React.useState('07:35');
  const [editMealTargetKcal, setEditMealTargetKcal] = React.useState('');
  const [mealSlotDialogMode, setMealSlotDialogMode] = React.useState<MealSlotDialogMode>('edit');
  const [isSavingMealSlot, setIsSavingMealSlot] = React.useState(false);
  const [selectedRecipeDetails, setSelectedRecipeDetails] = React.useState<SelectedRecipeDetailsContext | null>(null);
  const quantitySyncFieldRef = React.useRef<Record<string, QuantitySyncField | undefined>>({});
  const syncingQuantityKeysRef = React.useRef<Record<string, true>>({});
  const [, setSyncingQuantityVersion] = React.useState(0);
  const [qtyDraftByRowKey, setQtyDraftByRowKey] = React.useState<Record<string, string>>({});
  const getQuantityRowKey = React.useCallback((mealName: string, rowId: string) => `${mealName}::${rowId}`, []);
  const setRowQuantitySyncing = React.useCallback((mealName: string, rowId: string, syncing: boolean) => {
    const key = `${mealName}::${rowId}`;
    if (syncing) {
      if (syncingQuantityKeysRef.current[key]) return;
      syncingQuantityKeysRef.current[key] = true;
    } else if (!syncingQuantityKeysRef.current[key]) {
      return;
    } else {
      delete syncingQuantityKeysRef.current[key];
    }
    setSyncingQuantityVersion((version) => version + 1);
  }, []);
  const isRowQuantitySyncing = React.useCallback((mealName: string, rowId: string | undefined) => {
    if (!rowId) return false;
    return Boolean(syncingQuantityKeysRef.current[getQuantityRowKey(mealName, rowId)]);
  }, [getQuantityRowKey]);
  const getRowQtyDraft = React.useCallback(
    (mealName: string, rowId: string | undefined) => {
      if (!rowId) return undefined;
      return qtyDraftByRowKey[getQuantityRowKey(mealName, rowId)];
    },
    [getQuantityRowKey, qtyDraftByRowKey],
  );
  const beginInlineSave = React.useCallback(() => {
    setPendingInlineSaveCount((prev) => prev + 1);
  }, []);
  const endInlineSave = React.useCallback(() => {
    setPendingInlineSaveCount((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);
  const isInlineSaving = pendingInlineSaveCount > 0;
  const beginAddingItem = React.useCallback((rowKey: string, itemKey: string) => {
    setAddingItemByRowKey((prev) => ({ ...prev, [rowKey]: itemKey }));
  }, []);
  const endAddingItem = React.useCallback((rowKey: string) => {
    setAddingItemByRowKey((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);
  const isRowAddingItem = React.useCallback(
    (mealName: string, rowSearchId: string) => Boolean(addingItemByRowKey[buildAddingItemRowKey(mealName, rowSearchId)]),
    [addingItemByRowKey],
  );
  const isMealLevelAddingItem = React.useCallback(
    (mealName: string) => Boolean(addingItemByRowKey[buildAddingItemRowKey(mealName)]),
    [addingItemByRowKey],
  );
  const refreshClinicalInsights = React.useCallback(() => {
    setClinicalInsightsRefreshKey((key) => key + 1);
  }, []);
  const isAddItemPopoverAdding = React.useMemo(() => {
    if (!addItemMealName) return false;
    const mealPrefix = `${addItemMealName}::`;
    return Object.keys(addingItemByRowKey).some((rowKey) => rowKey.startsWith(mealPrefix));
  }, [addItemMealName, addingItemByRowKey]);
  const addItemPopoverAddingOption = React.useMemo(() => {
    if (!addItemMealName) return null;
    const mealPrefix = `${addItemMealName}::`;
    const match = Object.entries(addingItemByRowKey).find(([rowKey]) => rowKey.startsWith(mealPrefix));
    return match?.[1] ?? null;
  }, [addItemMealName, addingItemByRowKey]);
  const beginDeletingItem = React.useCallback((rowKey: string) => {
    setDeletingItemByRowKey((prev) => ({ ...prev, [rowKey]: true }));
  }, []);
  const endDeletingItem = React.useCallback((rowKey: string) => {
    setDeletingItemByRowKey((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);
  const isRowDeletingItem = React.useCallback(
    (mealName: string, rowSearchId: string) => Boolean(deletingItemByRowKey[buildAddingItemRowKey(mealName, rowSearchId)]),
    [deletingItemByRowKey],
  );
  const hasActiveRowDeletion = Object.keys(deletingItemByRowKey).length > 0;

  React.useEffect(() => {
    setSelectedDayNumber(dayNumberValue);
    setAvailableDayNumbers((prev) => (prev.includes(dayNumberValue) ? prev : [dayNumberValue]));
  }, [dayNumberValue]);

  React.useEffect(() => {
    setGenericSlotCheckboxChoice({});
    setMealRowCheckboxState({});
    setQtyDraftByRowKey({});
    qtyDraftByRowKeyRef.current = {};
    Object.values(qtyDraftCommitTimersRef.current).forEach((timer) => clearTimeout(timer));
    qtyDraftCommitTimersRef.current = {};
    syncingQuantityKeysRef.current = {};
    setSyncingQuantityVersion((version) => version + 1);
  }, [selectedDayNumber, resolvedPlanId]);

  React.useEffect(() => {
    rowsByMealRef.current = rowsByMeal;
  }, [rowsByMeal]);

  React.useEffect(() => {
    if (!isGenericOriginPlan) return;
    const mealMetaByName = new Map(mealMeta.map((meal) => [meal.name, meal]));
    setGenericSlotCheckboxChoice((prev) => {
      let changed = false;
      const next: Record<string, number | null> = { ...prev };

      Object.entries(rowsByMeal).forEach(([mealName, rows]) => {
        const pickableIndices = rows
          .map((row, index) => (isPickableNutritionRow(row) ? index : -1))
          .filter((index) => index >= 0);

        if (pickableIndices.length === 0) {
          if (next[mealName] !== null) {
            next[mealName] = null;
            changed = true;
          }
          return;
        }

        const currentChoice = next[mealName];
        const selectedOptionGroupId = String(mealMetaByName.get(mealName)?.selectedOptionGroupId ?? '').trim();
        const selectedChoiceIndex = selectedOptionGroupId
          ? pickableIndices.find((index) => String(rows[index]?.optionGroupId ?? '').trim() === selectedOptionGroupId)
          : undefined;
        const currentChoiceMatchesSelectedGroup =
          typeof currentChoice === 'number'
          && selectedOptionGroupId
          && String(rows[currentChoice]?.optionGroupId ?? '').trim() === selectedOptionGroupId;

        if (selectedChoiceIndex !== undefined && !currentChoiceMatchesSelectedGroup) {
          next[mealName] = selectedChoiceIndex;
          changed = true;
        } else if (currentChoice == null || !pickableIndices.includes(currentChoice)) {
          next[mealName] = selectedChoiceIndex ?? pickableIndices[0];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [isGenericOriginPlan, mealMeta, rowsByMeal]);

  React.useEffect(
    () => () => {
      Object.values(quantitySyncTimersRef.current).forEach((timer) => clearTimeout(timer));
      Object.values(qtyDraftCommitTimersRef.current).forEach((timer) => clearTimeout(timer));
      Object.values(rowItemSearchTimersRef.current).forEach((timer) => clearTimeout(timer));
      quantitySyncFieldRef.current = {};
      qtyDraftCommitTimersRef.current = {};
      syncingQuantityKeysRef.current = {};
      if (aiSaveTimerRef.current) {
        clearTimeout(aiSaveTimerRef.current);
      }
      if (daySelectorHighlightTimerRef.current) {
        clearTimeout(daySelectorHighlightTimerRef.current);
      }
    },
    [],
  );

  const loadDayData = React.useCallback(async () => {
    if (!resolvedPlanId) return;
    const loaded = await fetchNutritionalAnalysisDayData(resolvedPlanId, selectedDayNumber, {
      templateMode: isTemplateEditor && !isGenericPlan,
      genericMode: isGenericPlan,
    });
    if (!loaded && isGenericPlan) {
      throw new Error('Failed to load cohort meal plan');
    }
    if (!loaded) {
      setPlanName('');
      setLoadedTemplateKind(null);
      setLoadedTemplateCanEdit(true);
      setLoadedTemplateIsOwn(null);
      setLoadedTemplateVisibility(null);
      setApiMealMacroTotalsByMealName({});
      setApiDayMacroTotals(null);
      setApiTopCalorieContributors([]);
      setApiNutrientTotals({ fiber: 0, calcium: 0, iron: 0, vitaminD: 0 });
      setApiFatBreakdownTotals({ mufa: null, pufa: null, saturatedFat: null, transFat: null });
      setClinicalInsights(null);
      setClinicalInsightsError('');
      setDayNamesByNumber({});
      setPlanNotes(null);
      setPlanDescription(null);
      setDayNotes(null);
      setAppliedIntolerances([]);
      setAppliedAllergies([]);
      setAppliedCuisineTypes([]);
      setAppliedPreferences([]);
      setLoadedClinicalTags([]);
      setMealPlanTargetDailyActive(false);
      setMealPlanDisplayTargetFields({ calories: false, protein: false, carbs: false, fat: false });
      setImportedTemplateIds([]);
      return;
    }

    setPlanName(loaded.planName);
    setLoadedTemplateKind(loaded.templateKind);
    setLoadedTemplateCanEdit(loaded.templateCanEdit);
    setLoadedTemplateIsOwn(loaded.templateIsOwnTemplate);
    setLoadedTemplateVisibility(loaded.templateVisibility);
    setPlanStatus(loaded.planStatus);
    setPlanGenerationType(loaded.planGenerationType);
    setPlanGenerationSource(loaded.planGenerationSource);
    setPlanGenerationState(loaded.planGenerationState);
    if (loaded.availableDayNumbers.length > 0) {
      setAvailableDayNumbers(loaded.availableDayNumbers);
    }
    setDayNamesByNumber(loaded.dayNamesByNumber);
    setPlanNotes(loaded.planNotes ?? null);
    setPlanDescription(loaded.planDescription ?? null);
    setDayNotes(loaded.dayNotes ?? null);
    if (loaded.effectiveDayNumber !== selectedDayNumber) {
      setSelectedDayNumber(loaded.effectiveDayNumber);
    }
    setMealMeta(loaded.mealMeta);
    setRowsByMeal(loaded.rowsByMeal);
    rowsByMealRef.current = loaded.rowsByMeal;
    const canEditMerged =
      typeof routeCanEditTemplate === 'boolean' ? routeCanEditTemplate : loaded.templateCanEdit;
    const ownMerged =
      typeof routeTemplateIsOwn === 'boolean' ? routeTemplateIsOwn : loaded.templateIsOwnTemplate;
    const expandAllSlotsPublicView = computeIsPublicTemplateViewerLayout({
      isTemplateEditor,
      isGenericPlan,
      templateCanEdit: canEditMerged,
      effectiveIsOwn: ownMerged,
      templateVisibility: loaded.templateVisibility,
    });

    setExpandedCards((prev) => {
      if (expandAllSlotsPublicView) {
        const allOpen: Record<string, boolean> = {};
        loaded.mealMeta.forEach((meal) => {
          allOpen[meal.name] = true;
        });
        return allOpen;
      }
      const nextState = { ...loaded.expandedCards };
      // Preserve user's current open/closed state on refresh (e.g. after qty/unit/ingredient updates).
      Object.keys(nextState).forEach((mealName) => {
        if (typeof prev[mealName] === 'boolean') {
          nextState[mealName] = prev[mealName];
        }
      });
      return nextState;
    });
    setMacroTargets(loaded.macroTargets);
    setMealPlanTargetDailyActive(loaded.mealPlanTargetDailyActive);
    setMealPlanDisplayTargetFields(loaded.mealPlanDisplayTargetFields);
    setApiMealMacroTotalsByMealName(loaded.apiMealMacroTotalsByMealName);
    setApiDayMacroTotals(loaded.apiDayMacroTotals);
    setApiTopCalorieContributors(loaded.apiTopCalorieContributors);
    setApiNutrientTotals(loaded.apiNutrientTotals);
    setApiFatBreakdownTotals(loaded.apiFatBreakdownTotals);
    setAppliedIntolerances(uniquePreferenceValues([
      ...loaded.appliedIntolerances,
      ...routePreferredChoices.intolerances,
    ]));
    setAppliedAllergies(uniquePreferenceValues([
      ...loaded.appliedAllergies,
      ...routePreferredChoices.allergies,
    ]));
    setAppliedCuisineTypes(uniquePreferenceValues([
      ...loaded.appliedCuisineTypes,
      ...routePreferredChoices.cuisineTypes,
    ]));
    setAppliedPreferences(uniquePreferenceValues([
      ...loaded.appliedPreferences,
      ...routePreferredChoices.preferences,
    ]));
    setLoadedClinicalTags(loaded.clinicalTags);
    setImportedTemplateIds(loaded.importedTemplateIds);
    setDayDataLoadError('');
  }, [isGenericPlan, isTemplateEditor, resolvedPlanId, selectedDayNumber, routeCanEditTemplate, routeTemplateIsOwn, routePreferredChoices]);

  React.useEffect(() => {
    setIsDayDataLoading(true);
    setDayDataLoadError('');
    loadDayData().catch((error) => {
      if (isGenericPlan) {
        setDayDataLoadError(extractErrorMessage(error) || 'Failed to load cohort meal plan.');
      }
      setPlanName('');
      setLoadedTemplateCanEdit(true);
      setLoadedTemplateIsOwn(null);
      setLoadedTemplateVisibility(null);
      setPlanStatus('unknown');
      setPlanGenerationType('unknown');
      setPlanGenerationSource('unknown');
      setPlanGenerationState('unknown');
      const nextMealMeta = isGenericRouteContext ? [] : fallbackMealMeta;
      const nextRowsByMeal = isGenericRouteContext ? {} : fallbackRowsByMeal;
      setMealMeta(nextMealMeta);
      setRowsByMeal(nextRowsByMeal);
      rowsByMealRef.current = nextRowsByMeal;
      setExpandedCards(isGenericRouteContext ? {} : { 'Pre-Breakfast': true });
      setMacroTargets(defaultMacroTargets);
      setMealPlanTargetDailyActive(false);
      setMealPlanDisplayTargetFields({ calories: false, protein: false, carbs: false, fat: false });
      setApiMealMacroTotalsByMealName({});
      setApiDayMacroTotals(null);
      setApiTopCalorieContributors([]);
      setApiNutrientTotals({ fiber: 0, calcium: 0, iron: 0, vitaminD: 0 });
      setApiFatBreakdownTotals({ mufa: null, pufa: null, saturatedFat: null, transFat: null });
      setClinicalInsights(null);
      setClinicalInsightsError('');
      setDayNamesByNumber({});
      setAppliedIntolerances([]);
      setAppliedAllergies([]);
      setAppliedCuisineTypes([]);
      setAppliedPreferences([]);
      setLoadedClinicalTags([]);
    }).finally(() => {
      setIsDayDataLoading(false);
    });
  }, [isGenericRouteContext, loadDayData, dayDataReloadKey]);

  const summaryClinicalTags = clinicalTags ?? loadedClinicalTags;

  const mealPlanChoiceSegments = React.useMemo(() => {
    const allergies = uniquePreferenceValues(appliedAllergies);
    const intolerances = uniquePreferenceValues(appliedIntolerances);
    const cuisineTypes = uniquePreferenceValues([
      ...appliedCuisineTypes,
      ...appliedPreferences.filter(isCuisineTypeValue),
    ]);
    const preferences = uniquePreferenceValues(appliedPreferences)
      .filter((preference) => !isCuisineTypeValue(preference));

    return [
      allergies.length > 0 ? { label: 'Allergies', value: formatPreferenceDisplayList(allergies) } : null,
      intolerances.length > 0 ? { label: 'Intolerances', value: formatPreferenceDisplayList(intolerances) } : null,
      cuisineTypes.length > 0 ? { label: 'Cuisine Types', value: formatPreferenceDisplayList(cuisineTypes) } : null,
      preferences.length > 0 ? { label: 'Preferences', value: formatPreferenceDisplayList(preferences) } : null,
    ].filter((segment): segment is { label: string; value: string } => Boolean(segment));
  }, [appliedAllergies, appliedCuisineTypes, appliedIntolerances, appliedPreferences]);

  React.useEffect(() => {
    if (!shouldUseClientContext) {
      setManualDrvContext(null);
      setClientDetails(null);
      drvPrimaryTypesSourceRef.current = '';
      isLoadingDrvPrimaryTypesRef.current = false;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { client } = await getClientById(resolvedClientId, { force: true });
        if (cancelled) return;
        setClientDetails((client as Client) ?? null);

        const drvRequirements = client?.drvRequirements ?? null;
        drvPrimaryTypesSourceRef.current = '';
        setManualDrvContext({
          drvRequirements,
          primaryTypes: undefined,
          clientGoals: client?.clientGoals ?? null,
          eerKcal: isGenericOriginPlan ? resolveGenericClientEerKcal(client as Client | null | undefined) : undefined,
        });
      } catch {
        if (!cancelled) {
          setManualDrvContext(null);
          setClientDetails(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isGenericOriginPlan, resolvedClientId, shouldUseClientContext]);

  const ensureManualDrvPrimaryTypes = React.useCallback(async () => {
    if (!shouldUseClientContext || !clientDetails) return;
    const source = resolveClientDrvSource(clientDetails);
    if (drvPrimaryTypesSourceRef.current === source || isLoadingDrvPrimaryTypesRef.current) return;

    isLoadingDrvPrimaryTypesRef.current = true;
    try {
      const drvResponse = await getDrvPrimaryTypes({ source, limit: 200 });
      drvPrimaryTypesSourceRef.current = source;
      setManualDrvContext((current) => ({
        ...(current ?? {
          drvRequirements: clientDetails.drvRequirements ?? null,
          clientGoals: clientDetails.clientGoals ?? null,
          eerKcal: isGenericOriginPlan ? resolveGenericClientEerKcal(clientDetails) : undefined,
        }),
        primaryTypes: drvResponse.items ?? [],
      }));
    } catch {
      drvPrimaryTypesSourceRef.current = '';
    } finally {
      isLoadingDrvPrimaryTypesRef.current = false;
    }
  }, [clientDetails, isGenericOriginPlan, shouldUseClientContext]);

  const clientAllergiesForSearch = React.useMemo(
    () => Array.from(new Set(
      [
        ...appliedAllergies,
        ...(Array.isArray(clientDetails?.allergies) ? clientDetails.allergies : []),
      ]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean),
    )),
    [appliedAllergies, clientDetails?.allergies],
  );
  const allergySearchParam = React.useMemo(
    () => clientAllergiesForSearch.join(','),
    [clientAllergiesForSearch],
  );

  const nutritionAnalysisDrvContext = React.useMemo((): ManualNutritionDrvContext | null => {
    if (!mealPlanTargetDailyActive) return manualDrvContext;
    const base =
      manualDrvContext ?? {
        drvRequirements: null,
        primaryTypes: undefined as ManualNutritionDrvContext['primaryTypes'],
      };
    return {
      ...base,
      mealPlanDailyMacroTargets: macroTargets,
    };
  }, [manualDrvContext, mealPlanTargetDailyActive, macroTargets]);

  React.useEffect(() => {
    if (!isAddItemModalOpen) return;

    const normalizedQuery = addItemSearchValue.trim();
    if (normalizedQuery.length < 2) {
      setAddItemSearchResults([]);
      setAddItemSearchFilters(EMPTY_ADD_ITEM_SEARCH_FILTERS);
      setIsAddItemSearching(false);
      return;
    }

    setIsAddItemSearching(true);
    setAddItemSearchFilters(EMPTY_ADD_ITEM_SEARCH_FILTERS);

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchMealPlanItems({
          q: normalizedQuery,
          type: 'all',
          limit: 30,
          allergies: allergySearchParam || undefined,
          clientId: resolvedClientId || undefined,
        });
        if (isCancelled) return;

        setAddItemSearchResults(mapFoodSearchResults(response.results ?? []));
        setAddItemSearchFilters({
          allergies: normalizeSearchFilterValues(response.searchFilters?.allergies),
        });
      } catch {
        if (!isCancelled) {
          setAddItemSearchResults([]);
          setAddItemSearchFilters(EMPTY_ADD_ITEM_SEARCH_FILTERS);
        }
      } finally {
        if (!isCancelled) {
          setIsAddItemSearching(false);
        }
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [addItemSearchValue, allergySearchParam, isAddItemModalOpen, resolvedClientId]);

  React.useEffect(() => {
    if (!isAddItemModalOpen) return;
    const timeoutId = window.setTimeout(() => {
      addItemSearchInputRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isAddItemModalOpen]);

  const getNutritionRowCheckboxChecked = React.useCallback(
    (mealName: string, rowIndex: number) => {
      const rows = rowsByMeal[mealName] ?? [];
      const row = rows[rowIndex];
      if (!row || !isPickableNutritionRow(row)) return true;

      if (isGenericOriginPlan) {
        const pickableIndices = rows
          .map((r, i) => (isPickableNutritionRow(r) ? i : -1))
          .filter((i) => i >= 0);
        if (pickableIndices.length === 0) return true;
        const choice = genericSlotCheckboxChoice[mealName];
        const effectiveChoice = choice ?? pickableIndices[0];
        const choiceRow = rows[effectiveChoice];
        const choiceGroupId = String(choiceRow?.optionGroupId ?? '').trim();
        if (choiceGroupId) {
          return String(row.optionGroupId ?? '').trim() === choiceGroupId;
        }
        return effectiveChoice === rowIndex;
      }

      const mealMap = mealRowCheckboxState[mealName];
      if (!mealMap || Object.keys(mealMap).length === 0) {
        return true;
      }
      return Boolean(mealMap[rowIndex]);
    },
    [genericSlotCheckboxChoice, isGenericOriginPlan, mealRowCheckboxState, rowsByMeal],
  );

  const hasAnyPickableRows = React.useMemo(
    () => Object.values(rowsByMeal).some((rows) => rows.some(isPickableNutritionRow)),
    [rowsByMeal],
  );

  const rowsByMealForAnalysis = React.useMemo(() => {
    const result: Record<string, ExpandedFoodRow[]> = {};
    const names = new Set<string>();
    mealMeta.forEach((m) => names.add(m.name));
    Object.keys(rowsByMeal).forEach((k) => names.add(k));
    names.forEach((mealName) => {
      const rows = rowsByMeal[mealName] ?? [];
      result[mealName] = rows.filter((_, index) => getNutritionRowCheckboxChecked(mealName, index));
    });
    return result;
  }, [rowsByMeal, mealMeta, getNutritionRowCheckboxChecked]);

  const clinicalInsightsMealCompletion = React.useMemo(() => {
    const visibleMeals = [...mealMeta].sort((left, right) => left.sortOrder - right.sortOrder);
    const filledMeals = visibleMeals.filter((meal) => (
      (rowsByMealForAnalysis[meal.name] ?? []).some(hasClinicalInsightMealItem)
    ));
    return {
      total: visibleMeals.length,
      filled: filledMeals.length,
      allFilled: visibleMeals.length > 0 && filledMeals.length === visibleMeals.length,
    };
  }, [mealMeta, rowsByMealForAnalysis]);

  React.useEffect(() => {
    if (
      !resolvedPlanId
      || isGenericPlan
      || isDayDataLoading
      || !showAnalysisSidebar
      || !clinicalInsightsMealCompletion.allFilled
    ) {
      setClinicalInsights(null);
      setClinicalInsightsError('');
      setIsClinicalInsightsLoading(false);
      return;
    }

    let cancelled = false;
    setIsClinicalInsightsLoading(true);
    setClinicalInsightsError('');

    getMealPlanNutritionalAnalysis(String(resolvedPlanId))
      .then((response) => {
        if (cancelled) return;
        const data = response.data ?? response;
        setClinicalInsights(data?.clinicalInsights ?? null);
      })
      .catch((error) => {
        if (cancelled) return;
        setClinicalInsights(null);
        setClinicalInsightsError(extractErrorMessage(error) || 'Failed to load clinical insights.');
      })
      .finally(() => {
        if (!cancelled) {
          setIsClinicalInsightsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    clinicalInsightsMealCompletion.allFilled,
    clinicalInsightsRefreshKey,
    dayDataReloadKey,
    isDayDataLoading,
    isGenericPlan,
    resolvedPlanId,
    showAnalysisSidebar,
  ]);

  const mealCards = React.useMemo(() => {
    const sortedMeals = [...mealMeta].sort((left, right) => left.sortOrder - right.sortOrder);
    return sortedMeals.map((meal) => {
      const fullRows = rowsByMeal[meal.name] ?? [];
      const analysisRows = rowsByMealForAnalysis[meal.name] ?? [];
      const rowTotals = rowMacroTotals(analysisRows);
      const apiMeal = apiMealMacroTotalsByMealName[meal.name];
      const mealHasPickable = fullRows.some(isPickableNutritionRow);
      const totals = mealHasPickable ? rowTotals : apiMeal !== undefined ? apiMeal : rowTotals;
      return {
        ...meal,
        totals,
        kcalLabel: `${formatCalories(totals.calories)} kcal`,
        proteinLabel: `P:${formatNumber(totals.protein)}g`,
        carbsLabel: `C:${formatNumber(totals.carbs)}g`,
        fatLabel: `F:${formatNumber(totals.fat)}g`,
      };
    });
  }, [mealMeta, rowsByMeal, rowsByMealForAnalysis, apiMealMacroTotalsByMealName]);

  /** Whether each meal slot has at least one recipe/ingredient with content. */
  const slotHasRecipesByName = React.useMemo(() => {
    const result: Record<string, boolean> = {};
    mealMeta.forEach((meal) => {
      result[meal.name] = (rowsByMeal[meal.name] ?? []).some(isPickableNutritionRow);
    });
    return result;
  }, [mealMeta, rowsByMeal]);

  /** Reason the whole-day "Save as Template" is disabled, or null when it may be saved. */
  const mealPlanTemplateDisabledReason = React.useMemo(() => {
    if (mealMeta.length === 0) {
      return 'Add recipes to this meal plan before saving it as a template.';
    }
    const filledCount = mealMeta.reduce(
      (count, meal) => count + (slotHasRecipesByName[meal.name] ? 1 : 0),
      0,
    );
    if (filledCount === 0) {
      return 'Add recipes to this meal plan before saving it as a template.';
    }
    if (filledCount < mealMeta.length) {
      return 'Add recipes to all meal slots to enable saving this template.';
    }
    return null;
  }, [mealMeta, slotHasRecipesByName]);

  /** Sum of each meal's API slot rollup or row sums (same as meal cards); avoids `dayTotals.macros` rounding drift. */
  const totalIntake = React.useMemo(() => {
    if (apiDayMacroTotals && !hasAnyPickableRows) {
      return {
        calories: Math.max(0, apiDayMacroTotals.calories),
        protein: Math.max(0, apiDayMacroTotals.protein),
        carbs: Math.max(0, apiDayMacroTotals.carbs),
        fat: Math.max(0, apiDayMacroTotals.fat),
      };
    }
    return mealCards.reduce<MacroTotals>(
      (acc, meal) => ({
        calories: acc.calories + Math.max(0, meal.totals.calories),
        protein: acc.protein + Math.max(0, meal.totals.protein),
        carbs: acc.carbs + Math.max(0, meal.totals.carbs),
        fat: acc.fat + Math.max(0, meal.totals.fat),
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 },
    );
  }, [apiDayMacroTotals, mealCards, hasAnyPickableRows]);

  const effectiveFatBreakdownTotals = React.useMemo((): FatBreakdownTotals => {
    if (!hasAnyPickableRows) return apiFatBreakdownTotals;
    const flatRows = Object.values(rowsByMealForAnalysis).flat();
    const fromRows = sumExpandedFoodRowFatBreakdown(flatRows);
    const hasRowFatData =
      fromRows.mufa != null
      || fromRows.pufa != null
      || fromRows.saturatedFat != null
      || fromRows.transFat != null;
    return hasRowFatData ? fromRows : apiFatBreakdownTotals;
  }, [apiFatBreakdownTotals, hasAnyPickableRows, rowsByMealForAnalysis]);

  const displayMacroTargets = React.useMemo(
    () => resolveManualNutritionTargets(macroTargets, nutritionAnalysisDrvContext),
    [macroTargets, nutritionAnalysisDrvContext],
  );

  const sidebarMacroTargets = React.useMemo(
    () => ({
      ...displayMacroTargets,
      calories: mealPlanDisplayTargetFields.calories ? macroTargets.calories : displayMacroTargets.calories,
      protein: mealPlanDisplayTargetFields.protein ? macroTargets.protein : displayMacroTargets.protein,
      carbs: mealPlanDisplayTargetFields.carbs ? macroTargets.carbs : displayMacroTargets.carbs,
      fat: mealPlanDisplayTargetFields.fat ? macroTargets.fat : displayMacroTargets.fat,
    }),
    [displayMacroTargets, macroTargets, mealPlanDisplayTargetFields],
  );

  const hasMealPlanDisplayTargets = React.useMemo(
    () => Object.values(mealPlanDisplayTargetFields).some(Boolean),
    [mealPlanDisplayTargetFields],
  );

  const calorieTargetPercent =
    sidebarMacroTargets.calories > 0
      ? (totalIntake.calories / sidebarMacroTargets.calories) * 100
      : 0;

  const macroRows = React.useMemo(() => {
    const percentOfTarget = (total: number, target: number) =>
      target > 0 ? (total / target) * 100 : 0;
    const fatLimit = (nutritionAnalysisDrvContext?.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits?.fat;
    const fatStatus = fatLimit ? clinicalLimitStatus({ limit: fatLimit, intake: totalIntake.fat }) : null;
    return [
      {
        label: 'Carbs',
        total: totalIntake.carbs,
        target: sidebarMacroTargets.carbs,
        color: getNutritionTrafficLightColor(percentOfTarget(totalIntake.carbs, sidebarMacroTargets.carbs)),
      },
      {
        label: 'Protein',
        total: totalIntake.protein,
        target: sidebarMacroTargets.protein,
        color: getNutritionTrafficLightColor(
          percentOfTarget(totalIntake.protein, sidebarMacroTargets.protein),
        ),
      },
      {
        label: 'Fat',
        total: totalIntake.fat,
        target: sidebarMacroTargets.fat,
        color: fatStatus ? suggestedRdaToneStyles[fatStatus.tone].bar : getNutritionTrafficLightColor(percentOfTarget(totalIntake.fat, sidebarMacroTargets.fat)),
        clinicalLimitLabel: formatStructuredClinicalLimit(fatLimit),
      },
    ];
  }, [
    nutritionAnalysisDrvContext,
    sidebarMacroTargets.carbs,
    sidebarMacroTargets.fat,
    sidebarMacroTargets.protein,
    totalIntake.carbs,
    totalIntake.fat,
    totalIntake.protein,
  ]);

  const mealBreakdown = React.useMemo(() => {
    const totalCalories = totalIntake.calories;
    return mealCards.map((meal) => {
      const percent = totalCalories > 0 ? clampPercent((meal.totals.calories / totalCalories) * 100) : 0;
      const percentLabel = totalCalories > 0 ? ` (${roundNutritionDisplay(percent)}%)` : '';
      return {
        label: meal.name,
        value: `${formatCalories(meal.totals.calories)} kcal${percentLabel}`,
      };
    });
  }, [mealCards, totalIntake.calories]);

  const selectedClinicalInsightDay = React.useMemo(() => (
    clinicalInsights?.days?.find((day) => Number(day.dayNumber) === selectedDayNumber) ?? null
  ), [clinicalInsights?.days, selectedDayNumber]);

  const clinicalInsightContextLabels = React.useMemo(() => {
    const labels: string[] = [];
    const seen = new Set<string>();
    summaryClinicalTags.forEach((label) => pushUniqueClinicalInsightLabel(labels, seen, label));
    resolveClientClinicalInsightLabels(clientDetails).forEach((label) => {
      pushUniqueClinicalInsightLabel(labels, seen, label);
    });
    return labels;
  }, [clientDetails, summaryClinicalTags]);

  const sidebarClinicalInsights = React.useMemo(() => {
    if (isGenericPlan) {
      return null;
    }
    return {
      items: selectedClinicalInsightDay?.items ?? clinicalInsights?.overall?.items ?? [],
      matchedConditions: clinicalInsights?.matchedConditions ?? [],
      biomarkerOverlays: clinicalInsights?.biomarkerOverlays ?? [],
      contextLabels: clinicalInsightContextLabels,
      completion: {
        filled: clinicalInsightsMealCompletion.filled,
        total: clinicalInsightsMealCompletion.total,
      },
      isReady: clinicalInsightsMealCompletion.allFilled,
      hasBackendPayload: Boolean(clinicalInsights),
      isLoading: isClinicalInsightsLoading,
      error: clinicalInsightsError,
    };
  }, [
    clinicalInsights?.biomarkerOverlays,
    clinicalInsights?.matchedConditions,
    clinicalInsights?.overall?.items,
    clinicalInsightsError,
    clinicalInsightContextLabels,
    clinicalInsightsMealCompletion.allFilled,
    clinicalInsightsMealCompletion.filled,
    clinicalInsightsMealCompletion.total,
    isClinicalInsightsLoading,
    isGenericPlan,
    clinicalInsights,
    selectedClinicalInsightDay?.items,
  ]);

  const selectedTemplateMeal = React.useMemo(() => {
    if (templateSaveTarget?.kind !== 'meal_slot') return null;
    return mealCards.find((meal) => meal.name === templateSaveTarget.mealName) ?? null;
  }, [mealCards, templateSaveTarget]);

  const saveTemplateKind = templateSaveTarget?.kind ?? 'meal_slot';
  const saveTemplateModalLabel =
    templateSaveTarget?.kind === 'meal_plan'
      ? (planName.trim() || 'Meal Plan')
      : (selectedTemplateMeal?.name ?? 'Meal Slot');
  const saveTemplateSummaryDetail =
    templateSaveTarget?.kind === 'meal_plan'
      ? `${dayLabel || `Day ${selectedDayNumber}`} · ${mealCards.length} meal slots`
      : [
          selectedTemplateMeal?.time,
          selectedTemplateMeal?.kcalLabel,
        ].filter(Boolean).join(' · ') || 'Meal slot template';
  const saveTemplateMealCount = templateSaveTarget?.kind === 'meal_plan' ? mealCards.length : 1;
  const saveTemplateTotalCalories =
    templateSaveTarget?.kind === 'meal_plan'
      ? totalIntake.calories
      : (selectedTemplateMeal?.totals.calories ?? 0);

  const [manualAnalysisDrawerOpen, setManualAnalysisDrawerOpen] = React.useState(false);
  const handleOpenManualAnalysisDrawer = React.useCallback(() => {
    trackMealPlanAnalyseClicked({
      product_area: 'meal_plans',
      meal_plan_surface: isGenericPlan ? 'generic_nutrition' : 'manual_draft',
    });
    setManualAnalysisDrawerOpen(true);
    void ensureManualDrvPrimaryTypes();
  }, [ensureManualDrvPrimaryTypes, isGenericPlan]);

  const manualAnalysisSnapshot = React.useMemo(
    () => {
      const analysisDrvContext = hasMealPlanDisplayTargets
        ? ({
            ...(nutritionAnalysisDrvContext ?? {
              drvRequirements: null,
              primaryTypes: undefined as ManualNutritionDrvContext['primaryTypes'],
            }),
            mealPlanDailyMacroTargets: sidebarMacroTargets,
            preferMealPlanMacroTargets: true,
          } satisfies ManualNutritionDrvContext)
        : nutritionAnalysisDrvContext;

      return buildManualNutritionAnalysisSnapshot(
        dayLabel,
        mealCards,
        totalIntake,
        sidebarMacroTargets,
        rowsByMealForAnalysis,
        analysisDrvContext,
        hasAnyPickableRows ? null : apiTopCalorieContributors,
        apiNutrientTotals,
        effectiveFatBreakdownTotals,
      );
    },
    [dayLabel, mealCards, totalIntake, sidebarMacroTargets, rowsByMealForAnalysis, hasMealPlanDisplayTargets, nutritionAnalysisDrvContext, hasAnyPickableRows, apiTopCalorieContributors, apiNutrientTotals, effectiveFatBreakdownTotals],
  );

  const handleViewDetailedAnalysis = React.useCallback(() => {
    if (!shouldUseClientContext || !resolvedClientId) return;

    trackMealPlanDetailedReportClicked({
      product_area: 'meal_plans',
      meal_plan_surface: isGenericPlan ? 'generic_nutrition' : 'manual_draft',
    });

    const query = new URLSearchParams();
    if (resolvedPlanId) query.set('mealPlanId', String(resolvedPlanId));
    if (selectedDayNumber > 0) query.set('day', String(selectedDayNumber));

    const analysisBackPath = `${location.pathname}${location.search}`;

    setManualAnalysisDrawerOpen(false);
    navigate(
      `/clients/${encodeURIComponent(resolvedClientId)}/details-v2/analysis${query.toString() ? `?${query.toString()}` : ''}`,
      { state: { analysisBackPath } },
    );
  }, [location.pathname, location.search, navigate, resolvedClientId, resolvedPlanId, selectedDayNumber, shouldUseClientContext]);

  const toggleMealCard = React.useCallback((mealName: string) => {
    setExpandedCards((prev) => ({ ...prev, [mealName]: !prev[mealName] }));
  }, []);

  const templateCanEdit =
    typeof routeCanEditTemplate === 'boolean' ? routeCanEditTemplate : loadedTemplateCanEdit;
  const templateInteractionDisabled = isTemplateEditor && !isGenericPlan && !templateCanEdit;
  const effectiveTemplateIsOwn =
    typeof routeTemplateIsOwn === 'boolean' ? routeTemplateIsOwn : loadedTemplateIsOwn;
  const isPublicTemplateViewerLayout = React.useMemo(
    () =>
      computeIsPublicTemplateViewerLayout({
        isTemplateEditor,
        isGenericPlan,
        templateCanEdit,
        effectiveIsOwn: effectiveTemplateIsOwn,
        templateVisibility: loadedTemplateVisibility,
      }),
    [
      effectiveTemplateIsOwn,
      isGenericPlan,
      isTemplateEditor,
      loadedTemplateVisibility,
      templateCanEdit,
    ],
  );
  const canEditMealPlanNotes =
    Boolean(resolvedPlanId)
    && !templateInteractionDisabled
    && !isPublicTemplateViewerLayout;

  const handleSaveMealPlanNotes = React.useCallback(
    async (request: UpdateMealPlanNotesRequest): Promise<boolean> => {
      if (!resolvedPlanId || !canEditMealPlanNotes) return false;
      setIsSavingMealPlanNotes(true);
      try {
        const response = await updateMealPlanNotes(resolvedPlanId, request);
        if (response?.success === false) {
          throw new Error(response?.message || 'Unable to update notes');
        }

        const nextNotes =
          request.notes == null || String(request.notes).trim() === ''
            ? null
            : String(request.notes);

        if (request.level === 'plan') {
          if (planStatus === 'draft') {
            setPlanNotes(nextNotes);
          } else {
            setPlanDescription(nextNotes);
          }
        } else if (request.level === 'day') {
          setDayNotes(nextNotes);
        } else if (request.level === 'meal') {
          setMealMeta((prev) =>
            prev.map((meal) =>
              meal.apiMealName === request.mealName || meal.name === request.mealName
                ? { ...meal, notes: nextNotes }
                : meal,
            ),
          );
        } else if (request.level === 'item') {
          setRowsByMeal((prev) => {
            const next: Record<string, ExpandedFoodRow[]> = {};
            Object.entries(prev).forEach(([mealName, rows]) => {
              next[mealName] = rows.map((row) => {
                if ('itemId' in request && request.itemId && row.itemId === request.itemId) {
                  return { ...row, notes: nextNotes };
                }
                if (
                  'recipeId' in request
                  && request.recipeId
                  && row.recipeId === request.recipeId
                  && (!('day' in request) || request.day === selectedDayNumber)
                ) {
                  return { ...row, notes: nextNotes };
                }
                return row;
              });
            });
            rowsByMealRef.current = next;
            return next;
          });
        }

        await loadDayData().catch(() => undefined);
        showNotesSavedToast(request.level, nextNotes);
        return true;
      } catch (error) {
        showErrorToast(error, 'Unable to update notes');
        return false;
      } finally {
        setIsSavingMealPlanNotes(false);
      }
    },
    [canEditMealPlanNotes, loadDayData, planStatus, resolvedPlanId, selectedDayNumber],
  );

  const canDragMealSlots =
    (planStatus === 'draft' || isTemplateEditor)
    && !templateInteractionDisabled
    && !isSavingMealSlot
    && !isInlineSaving
    && !isReorderingMealSlots;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: {
        start: ['Enter'],
        cancel: ['Escape'],
        end: ['Enter', 'Tab'],
      },
    }),
  );

  const mealSlotDragId = React.useCallback((mealName: string) => `meal-slot-${mealName}`, []);
  const mealNameFromDragId = React.useCallback(
    (dragId: string) => dragId.replace(/^meal-slot-/, ''),
    [],
  );

  const handleMealCardDragStart = React.useCallback((event: DragStartEvent) => {
    if (!canDragMealSlots) return;
    const sourceMealName = mealNameFromDragId(String(event.active.id ?? ''));
    setDraggingMealName(sourceMealName || null);
  }, [canDragMealSlots, mealNameFromDragId]);

  const handleMealCardDragEnd = React.useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    const sourceMealName = mealNameFromDragId(String(active.id ?? ''));
    const targetMealName = mealNameFromDragId(String(over?.id ?? ''));
    setDraggingMealName(null);

    if (!canDragMealSlots || !over || !sourceMealName || !targetMealName || sourceMealName === targetMealName) return;

    const sortedMeals = [...mealMeta].sort((left, right) => left.sortOrder - right.sortOrder);
    const sourceIndex = sortedMeals.findIndex((meal) => meal.name === sourceMealName);
    const targetIndex = sortedMeals.findIndex((meal) => meal.name === targetMealName);
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return;
    }

    const reordered = arrayMove(sortedMeals, sourceIndex, targetIndex);
    const orderedMealOrders = reordered.map((meal) => Number(meal.sortOrder) || 0).filter((order) => order > 0);
    const reorderedWithDisplayOrder = reordered.map((meal, index) => ({
      ...meal,
      sortOrder: index + 1,
    }));

    setMealMeta(reorderedWithDisplayOrder);

    if (!resolvedPlanId || selectedDayNumber <= 0 || orderedMealOrders.length === 0) return;

    setIsReorderingMealSlots(true);
    try {
      const response = await reorderManualMealSlots(resolvedPlanId, selectedDayNumber, orderedMealOrders);
      if (!response?.success) {
        throw new Error(response?.message || 'Unable to reorder meal slots');
      }
      await loadDayData();
      refreshClinicalInsights();
      showSuccessToast('Meal slots reordered');
      await onMealPlanUpdated?.();
    } catch (error) {
      showErrorToast(error, 'Unable to reorder meal slots');
      await loadDayData().catch(() => undefined);
    } finally {
      setIsReorderingMealSlots(false);
    }
  }, [
    canDragMealSlots,
    loadDayData,
    mealNameFromDragId,
    mealMeta,
    onMealPlanUpdated,
    refreshClinicalInsights,
    resolvedPlanId,
    selectedDayNumber,
  ]);

  const resolveMealApiName = React.useCallback(
    (mealName: string): string => {
      const match = mealMeta.find((meal) => meal.name === mealName);
      return match?.apiMealName ?? mealName;
    },
    [mealMeta],
  );

  const isAIPlanType = React.useMemo(
    () => (
      planGenerationSource === 'ai' ||
      planGenerationType === 'ai_assisted' ||
      planGenerationType === 'ai_generated' ||
      planGenerationType === 'ai' ||
      planGenerationType === 'automated'
    ),
    [planGenerationSource, planGenerationType],
  );
  const isAiAssistedGeneration = React.useMemo(
    () => planGenerationType === 'ai_assisted',
    [planGenerationType],
  );

  const isAIDraftFlow = React.useMemo(() => {
    if (!isAIPlanType) return false;
    return planStatus === 'draft';
  }, [isAIPlanType, planStatus]);

  const mealPlanAlreadyFinalized = React.useMemo(
    () => isFinalizedMealPlanStatus(planStatus),
    [planStatus],
  );
  const canExportFullMealPlanPdf = React.useMemo(
    () => Boolean(resolvedPlanId) && (isGenericOriginPlan || isTemplateEditor || mealPlanAlreadyFinalized),
    [isGenericOriginPlan, isTemplateEditor, mealPlanAlreadyFinalized, resolvedPlanId],
  );
  const hideSaveChangesForFinalizedMealPlan = !isGenericPlan && !isTemplateEditor && mealPlanAlreadyFinalized;
  const saveChangesLabel = React.useMemo(() => {
    if (isInlineSaving || isAISaveSimulating) return 'Saving...';
    if (!isGenericPlan && !isTemplateEditor && !isAIDraftFlow) return 'Finalise Meal Plan';
    return 'Save Changes';
  }, [isAIDraftFlow, isAISaveSimulating, isGenericPlan, isInlineSaving, isTemplateEditor]);
  const showManualFinalizeAction =
    !templateInteractionDisabled
    && !isGenericOriginPlan
    && !isGenericPlan
    && !isTemplateEditor
    && !isAIDraftFlow
    && !mealPlanAlreadyFinalized;

  React.useEffect(() => {
    if (!showManualFinalizeAction || !resolvedPlanId || planStatus !== 'draft') {
      setManualFinalizeValidation(null);
      setIsValidatingManualFinalize(false);
      return;
    }

    let cancelled = false;
    setIsValidatingManualFinalize(true);
    const timer = window.setTimeout(() => {
      validateManualMealPlanFinalization(resolvedPlanId)
        .then((response) => {
          if (!cancelled) setManualFinalizeValidation(response.data);
        })
        .catch(() => {
          if (!cancelled) setManualFinalizeValidation(null);
        })
        .finally(() => {
          if (!cancelled) setIsValidatingManualFinalize(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [clinicalInsightsRefreshKey, dayDataReloadKey, planStatus, resolvedPlanId, showManualFinalizeAction]);

  const canDeleteMealSlots = React.useMemo(() => {
    if (isGenericOriginPlan && isFinalizedMealPlanStatus(planStatus)) return false;
    if (isGenericPlan) return planStatus === 'draft';
    if (isTemplateEditor && !isGenericPlan && !templateCanEdit) return false;
    if (isTemplateEditor) return true;
    if (planStatus === 'draft') return true;
    if (!isAIPlanType) return true;
    return isAIPlanType && planGenerationType === 'ai_assisted';
  }, [isAIPlanType, isGenericOriginPlan, isGenericPlan, isTemplateEditor, planGenerationType, planStatus, templateCanEdit]);

  const effectivePlanStatus = isGenericPlan && planStatus === 'draft'
    ? 'generic'
    : isTemplateEditor
      ? 'template'
      : isAIDraftFlow
        ? 'draft'
        : planStatus;
  const canShowSaveAsTemplateFlow = !isGenericOriginPlan && !isTemplateEditor && (showSaveAsTemplateFlow ?? true);
  const detailsV2MealPlanPath = resolvedClientId
    ? `/clients/${resolvedClientId}/details-v2/meal-plan?fromPage=1&fromLimit=10`
    : '/meal-planning';
  const normalizeMealTypeValue = React.useCallback((value: string): string => {
    const cleaned = String(value ?? '').trim().toLowerCase();
    if (!cleaned) return '';
    return cleaned.replace(/[\s-]+/g, '_').replace(/_+/g, '_');
  }, []);
  const toMealTypeLabel = React.useCallback((value: string): string => {
    return String(value ?? '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }, []);
  const deriveMealTypeFromMealMeta = React.useCallback((meta?: MealMeta | null): string => {
    if (!meta) return '';
    const fromMeta = normalizeMealTypeValue(meta.mealType ?? '');
    if (fromMeta) return fromMeta;
    const fromApiName = normalizeMealTypeValue(String(meta.apiMealName ?? '').replace(/_\d+$/, ''));
    if (fromApiName) return fromApiName;
    return normalizeMealTypeValue(meta.name ?? '');
  }, [normalizeMealTypeValue]);
  const mealTypeOptions = React.useMemo(() => FIXED_MEAL_TYPE_OPTIONS, []);
  const resolveAllowedMealType = React.useCallback((value: string): string => {
    const normalized = normalizeMealTypeValue(value);
    const matched = mealTypeOptions.find((option) => option.value === normalized);
    return matched?.value ?? 'other';
  }, [mealTypeOptions, normalizeMealTypeValue]);
  const buildDeleteMealSlotOptions = React.useCallback((meal: MealMeta): DeleteMealSlotOptions => {
    const mealOrder = Number(meal.sortOrder);
    const mealTime = mealDisplayTimeToApiHHMM(meal.time);
    const mealType = deriveMealTypeFromMealMeta(meal);

    return {
      ...(Number.isFinite(mealOrder) && mealOrder > 0 ? { mealOrder: Math.floor(mealOrder) } : {}),
      ...(mealType ? { mealType } : {}),
      ...(mealTime ? { mealTime, currentMealTime: mealTime } : {}),
    };
  }, [deriveMealTypeFromMealMeta]);
  const defaultMealTypeOptionValue = React.useMemo(
    () => mealTypeOptions[0]?.value ?? '',
    [mealTypeOptions],
  );

  const isExistingAIItemRow = React.useCallback(
    (_row: ExpandedFoodRow) => false,
    [],
  );

  const scrollDaySwitcherIntoView = React.useCallback(() => {
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    daySwitcherSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const focusDaySwitcherAfterFinalizeError = React.useCallback((missingDayNumber: number) => {
    if (availableDayNumbers.includes(missingDayNumber) && missingDayNumber !== selectedDayNumber) {
      setSelectedDayNumber(missingDayNumber);
    }

    setHighlightDaySelector(true);
    scrollDaySwitcherIntoView();

    if (daySelectorHighlightTimerRef.current) {
      clearTimeout(daySelectorHighlightTimerRef.current);
    }

    daySelectorHighlightTimerRef.current = setTimeout(() => {
      setHighlightDaySelector(false);
      daySelectorHighlightTimerRef.current = null;
    }, 2800);
  }, [availableDayNumbers, scrollDaySwitcherIntoView, selectedDayNumber]);

  React.useEffect(() => {
    if (!highlightDaySelector) return;

    const frameId = window.requestAnimationFrame(scrollDaySwitcherIntoView);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [highlightDaySelector, isDayDataLoading, scrollDaySwitcherIntoView, selectedDayNumber]);

  const handleSaveChanges = React.useCallback(async (overwriteConflicts = false) => {
    if (isInlineSaving) {
      showWarningToast('Saving in progress', 'Please wait for current updates to finish.');
      return;
    }
    if (isAIDraftFlow) {
      if (isAISaveSimulating) {
        return;
      }
      setIsAISaveSimulating(true);
      if (aiSaveTimerRef.current) {
        clearTimeout(aiSaveTimerRef.current);
      }
      aiSaveTimerRef.current = setTimeout(() => {
        setIsAISaveSimulating(false);
      }, 2000);
      return;
    }
    if (!resolvedPlanId) return;

    if (isTemplateEditor) {
      showSuccessToast('Template saved', 'Template changes are saved as you edit.');
      return;
    }

    if (isGenericPlan) {
      showSuccessToast('Cohort meal plan saved', 'Cohort meal plan changes are saved as you edit.');
      return;
    }

    if (planStatus !== 'draft') {
      showSuccessToast('Changes saved', 'Meal plan is already finalized.');
      return;
    }

    let clinicalWarnings: ManualFinalizeWarningIssue[] = [];
    setIsValidatingManualFinalize(true);
    try {
      const validationResponse = await validateManualMealPlanFinalization(resolvedPlanId);
      setManualFinalizeValidation(validationResponse.data);
      if (!validationResponse.data.canFinalize) {
        const firstMissingDay = validationResponse.data.missingMealSlots[0]?.dayNumber;
        if (firstMissingDay) focusDaySwitcherAfterFinalizeError(firstMissingDay);
        showWarningToast(
          validationResponse.data.allMealSlotsFilled
            ? 'Resolve the clinical hard blocks shown below before finalizing.'
            : 'Add at least one item to every meal slot before finalizing.',
          'Finalization blocked',
          { duration: 6000 },
        );
        return;
      }
      if ((validationResponse.data.warnings?.length || 0) > 0) {
        clinicalWarnings = validationResponse.data.warnings;
        const warningCount = countClinicalIssueGroups(clinicalWarnings);
        showWarningToast(
          `${warningCount} clinical warning group${warningCount === 1 ? '' : 's'} to review`,
          'Related items are combined below by day and meal.',
          { duration: 7000 },
        );
      }
    } catch (error) {
      showErrorToast(error, 'Could not run final safety checks');
      return;
    } finally {
      setIsValidatingManualFinalize(false);
    }

    trackMealPlanFinalizeClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'manual_draft',
    });

    beginInlineSave();
    try {
      const finalizedPlanName = String(planName ?? '').trim() || 'My Finalized Plan';
      const response = await finalizeManualMealPlan(resolvedPlanId, finalizedPlanName, undefined, overwriteConflicts);
      if (!response.success) {
        const responseError = String((response as { error?: unknown }).error ?? '').trim();
        throw new Error(responseError || response.message || 'Failed to finalize meal plan');
      }

      if (clinicalWarnings.length > 0) {
        const warningCount = countClinicalIssueGroups(clinicalWarnings);
        showWarningToast(
          `Meal plan finalized with ${warningCount} clinical warning group${warningCount === 1 ? '' : 's'}`,
          'The grouped clinical review remains available in the meal-plan analysis.',
          { duration: 7000 },
        );
      } else {
        showSuccessToast('Meal plan finalized', response.message || 'Manual meal plan finalized successfully.');
      }
      await onMealPlanUpdated?.();

      const finalizedData =
        response.data && typeof response.data === 'object'
          ? (response.data as Record<string, unknown>)
          : null;
      const finalizedPlanId = String(
        finalizedData?.mealPlanId ?? finalizedData?.planId ?? finalizedData?.id ?? '',
      ).trim();

      if (!embedded && finalizedPlanId && finalizedPlanId !== resolvedPlanId && resolvedClientId) {
        const fromQuery = safeFromPath ? `?from=${encodeURIComponent(safeFromPath)}` : '';
        navigate(
          `/meal-plans/${finalizedPlanId}/clients/${resolvedClientId}/nutrition/${selectedDayNumber}${fromQuery}`,
          { replace: true },
        );
        return;
      }

      await loadDayData();
      refreshClinicalInsights();
    } catch (error) {
      const conflicts = extractDateConflicts(error);
      if (conflicts.length > 0) {
        setFinalizeDateConflicts(conflicts);
        return;
      }
      const missingDayNumber = extractMissingMealDayNumber(error);
      if (missingDayNumber) {
        focusDaySwitcherAfterFinalizeError(missingDayNumber);
        showWarningToast(buildMissingMealDayMessage(missingDayNumber), undefined, { duration: 6000 });
        return;
      }
      const validation = (error as { response?: { data?: { details?: { validation?: ManualFinalizeValidation } } } })?.response?.data?.details?.validation;
      if (validation) setManualFinalizeValidation(validation);
      showErrorToast(error, 'Unable to finalize meal plan');
    } finally {
      endInlineSave();
    }
  }, [
    beginInlineSave,
    resolvedClientId,
    embedded,
    endInlineSave,
    focusDaySwitcherAfterFinalizeError,
    isAISaveSimulating,
    isAIDraftFlow,
    isGenericPlan,
    isInlineSaving,
    isTemplateEditor,
    loadDayData,
    navigate,
    onMealPlanUpdated,
    planName,
    planStatus,
    resolvedPlanId,
    refreshClinicalInsights,
    safeFromPath,
    selectedDayNumber,
  ]);

  const handleExportMealPlanPdf = React.useCallback(async () => {
    if (!resolvedPlanId || !canExportFullMealPlanPdf || isExportingPdf) return;

    setIsExportingPdf(true);
    try {
      const pdfOptions = {
        planTitle: planName.trim() || undefined,
        clinicName: user?.practiceName ?? undefined,
        clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
      };

      if (isTemplateEditor && !isGenericPlan) {
        await downloadMealPlanTemplatePdfFromExportApi(resolvedPlanId, pdfOptions);
        showSuccessToast('Template exported');
      } else if (isGenericPlan || (isGenericOriginPlan && planStatus === 'generic')) {
        await downloadGenericMealPlanPdfFromExportApi(resolvedPlanId, pdfOptions);
        showSuccessToast('Meal plan exported');
      } else {
        await downloadMealPlanPdfFromExportApi(resolvedPlanId, pdfOptions);
        showSuccessToast('Meal plan exported');
      }
    } catch (error) {
      showErrorToast(error, 'Failed to download PDF');
    } finally {
      setIsExportingPdf(false);
    }
  }, [
    canExportFullMealPlanPdf,
    isGenericOriginPlan,
    isGenericPlan,
    isExportingPdf,
    isTemplateEditor,
    planName,
    planStatus,
    resolvedPlanId,
    user?.clinicLogoUrl,
    user?.practiceName,
  ]);

  const getRecipeDownloadRowKey = React.useCallback(
    (mealName: string, rowIndex: number, row: ExpandedFoodRow) => {
      const rowIdentifier = String(row.itemId ?? row.recipeId ?? row.rowId ?? row.item ?? rowIndex).trim();
      return `${mealName}::${rowIdentifier || rowIndex}`;
    },
    [],
  );

  const handleDownloadRecipePdf = React.useCallback(
    async (mealName: string, rowIndex: number, row: ExpandedFoodRow) => {
      if (!resolvedPlanId || !canExportMealPlanPdf) {
        showWarningToast('Recipe export unavailable', 'Please open a saved meal plan or template before downloading a recipe.');
        return;
      }

      if (row.itemType !== 'recipe' || isIngredientRow(row)) {
        showWarningToast('Recipe export unavailable', 'Only recipe rows can be downloaded.');
        return;
      }

      if (downloadingRecipeRowKey) return;

      const recipeItemId = String(row.itemId ?? '').trim();
      const recipeId = String(row.recipeId ?? '').trim();
      const recipeName = String(row.item ?? '').trim();
      if (!recipeItemId && !recipeId && !recipeName) {
        showWarningToast('Recipe export unavailable', 'Recipe details are missing for this row.');
        return;
      }

      const rowKey = getRecipeDownloadRowKey(mealName, rowIndex, row);
      setDownloadingRecipeRowKey(rowKey);
      try {
        const shouldUseRecipeItemIdForExport = (isTemplateEditor && !isGenericPlan) || mealPlanAlreadyFinalized;
        const exportRecipeItemId = shouldUseRecipeItemIdForExport ? recipeItemId : '';
        const recipePdfOptions = {
          recipeItemId: exportRecipeItemId || undefined,
          recipeId: recipeId || undefined,
          recipeName: recipeName || undefined,
          mealName,
          recipeLookupMealName: resolveMealApiName(mealName),
          dayNumber: selectedDayNumber,
          planTitle: recipeName || planName.trim() || undefined,
          clinicName: user?.practiceName ?? undefined,
          clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
          nutritionSummary: {
            calories: numberFromUnknown(row.kcal),
            carbs: numberFromUnknown(row.carbs),
            protein: numberFromUnknown(row.protein),
            fat: numberFromUnknown(row.fat),
          },
        };

        if (isTemplateEditor && !isGenericPlan) {
          await downloadTemplateRecipeDetailsPdfFromExportApi(resolvedPlanId, recipePdfOptions);
        } else if (!mealPlanAlreadyFinalized) {
          await downloadDraftRecipeDetailsPdfFromRecipeApi(resolvedPlanId, {
            ...recipePdfOptions,
            recipeItemId: recipeItemId || undefined,
          });
        } else {
          await downloadRecipeDetailsPdfFromExportApi(resolvedPlanId, recipePdfOptions);
        }
        showSuccessToast('Recipe exported');
      } catch (error) {
        showErrorToast(error, 'Failed to download recipe PDF');
      } finally {
        setDownloadingRecipeRowKey((current) => (current === rowKey ? null : current));
      }
    },
    [
      canExportMealPlanPdf,
      downloadingRecipeRowKey,
      getRecipeDownloadRowKey,
      isGenericPlan,
      isTemplateEditor,
      mealPlanAlreadyFinalized,
      planName,
      resolvedPlanId,
      resolveMealApiName,
      selectedDayNumber,
      user?.clinicLogoUrl,
      user?.practiceName,
    ],
  );

  const isDraftGenerationPending = React.useMemo(
    () => planGenerationState === 'queued' || planGenerationState === 'in_progress',
    [planGenerationState],
  );

  const resolveAIDraftRecipeContext = React.useCallback(
    (mealName: string, targetRow?: ExpandedFoodRow): { mealOrder: number; recipeId: string } | null => {
      const mealOrderRaw = mealMeta.find((meal) => meal.name === mealName)?.sortOrder;
      const mealOrder = Number(mealOrderRaw);
      if (!Number.isFinite(mealOrder) || mealOrder < 1) return null;

      const rows = rowsByMealRef.current[mealName] ?? [];
      const fallbackRecipeRow = rows.find(
        (row) => !isIngredientRow(row) && String(row.recipeId ?? row.itemId ?? '').trim().length > 0,
      );
      const recipeId = String(
        (!isIngredientRow(targetRow ?? { itemType: undefined, ingredientId: undefined })
          ? targetRow?.recipeId ?? targetRow?.itemId
          : undefined)
        ?? fallbackRecipeRow?.recipeId
        ?? fallbackRecipeRow?.itemId
        ?? '',
      ).trim();
      if (!recipeId) return null;

      return { mealOrder, recipeId };
    },
    [mealMeta],
  );

  const handleDeleteMealSlotConfirm = React.useCallback(
    async (meal: MealMeta) => {
      if (!canDeleteMealSlots || !resolvedPlanId) {
        showWarningToast('Deleting meal slots is only available for editable meal plans.');
        return;
      }
      if (mealSlotDeleteInFlightRef.current) return;

      const mealName = meal.name;
      const apiMealName = String(meal.apiMealName ?? '').trim() || resolveMealApiName(mealName);
      mealSlotDeleteInFlightRef.current = true;
      setMealSlotDeletingName(mealName);
      try {
        const response = isGenericPlan
          ? await deleteGenericMealSlot(resolvedPlanId, apiMealName)
          : await deleteMealSlot(resolvedPlanId, selectedDayNumber, apiMealName, buildDeleteMealSlotOptions(meal));
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete meal slot');
        }
        await loadDayData();
        refreshClinicalInsights();
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to delete meal slot');
      } finally {
        mealSlotDeleteInFlightRef.current = false;
        setMealSlotDeletingName(null);
      }
    },
    [
      buildDeleteMealSlotOptions,
      canDeleteMealSlots,
      isGenericPlan,
      loadDayData,
      onMealPlanUpdated,
      refreshClinicalInsights,
      resolveMealApiName,
      resolvedPlanId,
      selectedDayNumber,
    ],
  );

  const handleDeleteMealSlotRequest = React.useCallback((meal: MealMeta) => {
    if (!canDeleteMealSlots) {
      showWarningToast('Deleting meal slots is only available for editable meal plans.');
      return;
    }
    setPendingDeleteMealSlot(meal);
    setPendingDeleteMealSlotName(meal.name);
    setDeleteMealSlotConfirmOpen(true);
  }, [canDeleteMealSlots]);

  const handleDeleteMealSlotConfirmSubmit = React.useCallback(async () => {
    const meal = pendingDeleteMealSlot;
    if (!meal) return;
    setDeleteMealSlotConfirmOpen(false);
    await handleDeleteMealSlotConfirm(meal);
    setPendingDeleteMealSlot(null);
    setPendingDeleteMealSlotName(null);
  }, [handleDeleteMealSlotConfirm, pendingDeleteMealSlot]);

  const getNextMealSlotName = React.useCallback(() => {
    const existingNames = new Set(
      mealMeta.map((meal) => String(meal.name ?? '').trim().toLowerCase()).filter(Boolean),
    );
    let index = 1;
    while (existingNames.has(`meal ${index}`)) {
      index += 1;
    }
    return `Meal ${index}`;
  }, [mealMeta]);

  const openAddMealSlotDialog = React.useCallback(() => {
    if (!canDeleteMealSlots) {
      showWarningToast('Adding meal slots is only available for editable meal plans.');
      return;
    }
    const nextMealName = getNextMealSlotName();
    setMealSlotDialogMode('add');
    setEditMealName(nextMealName);
    setEditMealOriginalName('');
    setEditMealOriginalTime('');
    setEditMealType(resolveAllowedMealType(nextMealName || defaultMealTypeOptionValue));
    setEditMealTime('07:30');
    setEditMealTargetKcal('');
    setEditMealDialogOpen(true);
  }, [canDeleteMealSlots, defaultMealTypeOptionValue, getNextMealSlotName, resolveAllowedMealType]);

  const openEditMealSlotDialog = React.useCallback((mealRowKey: string) => {
    const meta = mealMeta.find((meal) => meal.name === mealRowKey);
    if (!meta) return;
    setMealSlotDialogMode('edit');
    setEditMealName(meta.name);
    setEditMealOriginalName(resolveMealApiName(mealRowKey));
    const normalizedOriginalTime = mealDisplayTimeToApiHHMM(meta.time);
    setEditMealOriginalTime(normalizedOriginalTime);
    setEditMealType(resolveAllowedMealType(deriveMealTypeFromMealMeta(meta)));
    setEditMealTime(normalizedOriginalTime);
    setEditMealTargetKcal(meta.targetCalories != null && meta.targetCalories > 0 ? String(meta.targetCalories) : '');
    setEditMealDialogOpen(true);
  }, [deriveMealTypeFromMealMeta, mealMeta, resolveAllowedMealType, resolveMealApiName]);

  const closeEditMealSlotDialog = React.useCallback(() => {
    setEditMealDialogOpen(false);
  }, []);

  const handleSaveMealSlotEdit = React.useCallback(async () => {
    if (!resolvedPlanId) {
      showWarningToast('Plan unavailable', 'Please open a saved meal plan before editing meal slots.');
      return;
    }
    const trimmedName = editMealName.trim();
    if (!trimmedName) {
      showWarningToast('Meal name required', 'Enter a name for this meal slot.');
      return;
    }

    const targetTrimmed = editMealTargetKcal.trim();
    let targetCalories: number | null = null;
    if (targetTrimmed.length > 0) {
      const parsed = Number(targetTrimmed);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        showWarningToast('Invalid target calories', 'Enter a positive number or leave the field empty.');
        return;
      }
      targetCalories = parsed;
    }

    const apiTime = mealDisplayTimeToApiHHMM(editMealTime);
    const normalizedMealType = resolveAllowedMealType(editMealType);
    const mealTypeForRequest = isAIDraftFlow
      ? (normalizedMealType || normalizeMealTypeValue(trimmedName))
      : (normalizedMealType || undefined);
    if (isAIDraftFlow && !mealTypeForRequest) {
      showWarningToast('Meal type required', 'Please select a meal type for this AI meal slot.');
      return;
    }

    setIsSavingMealSlot(true);
    try {
      const response = isGenericPlan
        ? mealSlotDialogMode === 'add'
          ? await createGenericMealSlot(
              resolvedPlanId,
              trimmedName,
              apiTime,
              mealTypeForRequest || normalizeMealTypeValue(trimmedName) || 'snack',
              targetCalories,
            )
          : await updateGenericMealSlot(resolvedPlanId, editMealOriginalName || trimmedName, {
              newMealName: trimmedName,
              mealTime: apiTime,
              mealType: mealTypeForRequest || normalizeMealTypeValue(trimmedName) || 'snack',
              targetCalories,
            })
        : mealSlotDialogMode === 'add'
          ? await createMealSlot(resolvedPlanId, selectedDayNumber, trimmedName, apiTime, targetCalories, mealTypeForRequest)
          : await updateMealSlot(
            resolvedPlanId,
            selectedDayNumber,
            editMealOriginalName || trimmedName,
            apiTime,
            targetCalories,
            {
              mealType: mealTypeForRequest,
              newMealName: trimmedName,
              currentMealTime: editMealOriginalTime || apiTime,
            },
          );
      if (!response.success) {
        throw new Error(
          response.message || (mealSlotDialogMode === 'add' ? 'Failed to add meal slot' : 'Failed to update meal slot'),
        );
      }
      showSuccessToast(mealSlotDialogMode === 'add' ? 'Meal slot added' : 'Meal slot updated');
      closeEditMealSlotDialog();
      await loadDayData();
      refreshClinicalInsights();
      await onMealPlanUpdated?.();
    } catch (error) {
      showErrorToast(error, mealSlotDialogMode === 'add' ? 'Unable to add meal slot' : 'Unable to update meal slot');
    } finally {
      setIsSavingMealSlot(false);
    }
  }, [
    closeEditMealSlotDialog,
    mealSlotDialogMode,
    selectedDayNumber,
    editMealName,
    editMealOriginalName,
    editMealOriginalTime,
    editMealType,
    editMealTargetKcal,
    editMealTime,
    isAIDraftFlow,
    isGenericPlan,
    loadDayData,
    normalizeMealTypeValue,
    onMealPlanUpdated,
    refreshClinicalInsights,
    resolvedPlanId,
  ]);

  const handleDeleteRow = React.useCallback(
    async (mealName: string, rowIndex: number) => {
      const rows = rowsByMealRef.current[mealName] ?? [];
      const row = rows[rowIndex];
      if (!row) return;

      const ensuredRowId = row.rowId ?? resolveRowId(mealName, rowIndex, row.itemId, row.item);
      const timerKey = getQuantityRowKey(mealName, ensuredRowId);
      if (quantitySyncTimersRef.current[timerKey]) {
        clearTimeout(quantitySyncTimersRef.current[timerKey]);
        delete quantitySyncTimersRef.current[timerKey];
      }
      if (qtyDraftCommitTimersRef.current[timerKey]) {
        clearTimeout(qtyDraftCommitTimersRef.current[timerKey]);
        delete qtyDraftCommitTimersRef.current[timerKey];
      }
      delete quantitySyncFieldRef.current[timerKey];
      setRowQuantitySyncing(mealName, ensuredRowId, false);
      setQtyDraftByRowKey((prev) => {
        if (!prev[timerKey]) return prev;
        const next = { ...prev };
        delete next[timerKey];
        return next;
      });

      const removeLocally = () => {
        setRowsByMeal((prev) => {
          const mealRows = prev[mealName] ?? [];
          let nextRows = mealRows.filter((_, i) => i !== rowIndex);
          const apiMealName = mealMeta.find((m) => m.name === mealName)?.apiMealName ?? mealName;
          nextRows = ensureDefaultEmptyRowsForMeal(apiMealName, nextRows);
          const nextState = { ...prev, [mealName]: nextRows };
          rowsByMealRef.current = nextState;
          return nextState;
        });
      };

      const itemIdToRemove = String(row.itemId ?? row.ingredientId ?? row.recipeId ?? '').trim();
      if (!itemIdToRemove || !resolvedPlanId) {
        removeLocally();
        return;
      }

      const deletingRowKey = buildAddingItemRowKey(mealName, ensuredRowId);
      const apiItemType: 'recipe' | 'ingredient' =
        row.itemType === 'ingredient' || Boolean(row.ingredientId?.trim()) ? 'ingredient' : 'recipe';

      beginDeletingItem(deletingRowKey);
      try {
        const response = isTemplateEditor
          ? await removeMealPlanTemplateItem(resolvedPlanId, { itemId: itemIdToRemove })
          : isGenericPlan
            ? await removeGenericMealPlanItem({
                genericPlanId: resolvedPlanId,
                mealName: resolveMealApiName(mealName),
                itemType: apiItemType,
                itemId: itemIdToRemove,
              })
          : await removeManualMealPlanItem({
              draftId: resolvedPlanId,
              day: selectedDayNumber,
              mealName: resolveMealApiName(mealName),
              itemType: apiItemType,
              itemId: itemIdToRemove,
            });
        if (!response.success) {
          throw new Error(response.message || 'Failed to remove item');
        }
        removeLocally();
        await loadDayData();
        refreshClinicalInsights();
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to remove item');
      } finally {
        endDeletingItem(deletingRowKey);
      }
    },
    [beginDeletingItem, endDeletingItem, getQuantityRowKey, mealMeta, selectedDayNumber, loadDayData, onMealPlanUpdated, effectivePlanStatus, isGenericPlan, isTemplateEditor, refreshClinicalInsights, resolveMealApiName, resolvedPlanId, setRowQuantitySyncing],
  );

  const syncRowQuantity = React.useCallback(
    async (mealName: string, rowId: string, syncField?: QuantitySyncField, quantityOverride?: number) => {
      const clearRowQuantitySyncing = () => {
        setRowQuantitySyncing(mealName, rowId, false);
      };

      if (!resolvedPlanId) {
        clearRowQuantitySyncing();
        return;
      }

      const rows = rowsByMealRef.current[mealName] ?? [];
      const targetRow = rows.find((row) => row.rowId === rowId);
      if (!targetRow) {
        clearRowQuantitySyncing();
        return;
      }

      const apiMealName = resolveMealApiName(mealName);
      const isDraftPlan = effectivePlanStatus === 'draft';
      const resolvedItemId = String(
        targetRow.itemId ?? targetRow.ingredientId ?? targetRow.recipeId ?? '',
      ).trim();
      const rawQuantity =
        quantityOverride != null && Number.isFinite(quantityOverride)
          ? Math.max(0, quantityOverride)
          : Math.max(0, Number(targetRow.qty) || 0);
      // API persists a minimum of 0.1; keep local QTY aligned so macros and display match.
      const quantity = rawQuantity > 0 ? rawQuantity : 0.1;
      if (!resolvedItemId) {
        clearRowQuantitySyncing();
        return;
      }

      const applySyncedQuantityToRows = (syncedQuantity: number) => {
        const roundedSyncedQty = Math.round(syncedQuantity * 10) / 10;
        setRowsByMeal((prev) => {
          const mealRows = prev[mealName];
          if (!mealRows?.length) return prev;

          let changed = false;
          const nextRows = mealRows.map((row, index) => {
            const candidateId = row.rowId ?? resolveRowId(mealName, index, row.itemId, row.item);
            if (candidateId !== rowId) return row;
            const roundedRowQty = Math.round((Number(row.qty) || 0) * 10) / 10;
            if (roundedRowQty === roundedSyncedQty) return row;
            changed = true;
            return { ...row, qty: roundedSyncedQty };
          });

          if (!changed) return prev;
          const nextState = { ...prev, [mealName]: nextRows };
          rowsByMealRef.current = nextState;
          return nextState;
        });
      };

      setRowQuantitySyncing(mealName, rowId, true);
      beginInlineSave();
      try {
        if (isDraftPlan && isAIDraftFlow) {
          await runAIDraftQuantitySync({
            resolvedPlanId,
            selectedDayNumber,
            mealName,
            targetRow,
            quantity,
            isDraftGenerationPending,
            resolveAIDraftRecipeContext,
            resolveMealApiName,
            parseRecipeServingId,
            defaultAIDraftIngredientServingId,
            isIngredientRow,
            syncField,
          });
        } else {
          await runManualOrFinalizedQuantitySync({
            resolvedPlanId,
            selectedDayNumber,
            planStatus: effectivePlanStatus,
            apiMealName,
            targetRow,
            rowsInMeal: rows,
            resolvedItemId,
            quantity,
            defaultAddIngredientServingId,
            parseRecipeServingId,
            isIngredientRow,
            syncField,
          });
        }

        await loadDayData();
        applySyncedQuantityToRows(quantity);
        refreshClinicalInsights();
      } catch (error) {
        showErrorToast(error, 'Unable to sync meal item');
        await loadDayData().catch(() => undefined);
      } finally {
        clearRowQuantitySyncing();
        endInlineSave();
      }
    },
    [
      beginInlineSave,
      endInlineSave,
      isAIDraftFlow,
      isDraftGenerationPending,
      effectivePlanStatus,
      selectedDayNumber,
      loadDayData,
      refreshClinicalInsights,
      resolveAIDraftRecipeContext,
      resolveMealApiName,
      resolvedPlanId,
      setRowQuantitySyncing,
    ],
  );

  const queueRowQuantitySync = React.useCallback(
    (mealName: string, rowId: string, syncField: QuantitySyncField) => {
      const timerKey = getQuantityRowKey(mealName, rowId);
      quantitySyncFieldRef.current[timerKey] = syncField;
      if (quantitySyncTimersRef.current[timerKey]) {
        clearTimeout(quantitySyncTimersRef.current[timerKey]);
      }
      quantitySyncTimersRef.current[timerKey] = setTimeout(() => {
        delete quantitySyncTimersRef.current[timerKey];
        const latestSyncField = quantitySyncFieldRef.current[timerKey];
        delete quantitySyncFieldRef.current[timerKey];
        void syncRowQuantity(mealName, rowId, latestSyncField);
      }, quantitySyncDelayMs);
    },
    [getQuantityRowKey, syncRowQuantity],
  );

  const queueRowItemSuggestions = React.useCallback((rowId: string, query: string) => {
    const normalizedQuery = query.trim();
    rowItemLatestQueryRef.current[rowId] = normalizedQuery;

    if (rowItemSearchTimersRef.current[rowId]) {
      clearTimeout(rowItemSearchTimersRef.current[rowId]);
    }

    if (normalizedQuery.length < 2) {
      setRowItemSearchLoadingById((prev) => ({ ...prev, [rowId]: false }));
      setRowItemSearchResultsById((prev) => ({ ...prev, [rowId]: [] }));
      return;
    }

    setRowItemSearchLoadingById((prev) => ({ ...prev, [rowId]: true }));

    rowItemSearchTimersRef.current[rowId] = setTimeout(async () => {
      try {
        const response = await searchMealPlanItems({
          q: normalizedQuery,
          type: 'all',
          limit: 30,
          allergies: allergySearchParam || undefined,
          clientId: resolvedClientId || undefined,
        });

        if (rowItemLatestQueryRef.current[rowId] !== normalizedQuery) return;
        const mapped = mapFoodSearchResults(response.results ?? []);
        setRowItemSearchResultsById((prev) => ({ ...prev, [rowId]: mapped }));
      } catch {
        setRowItemSearchResultsById((prev) => ({ ...prev, [rowId]: [] }));
      } finally {
        if (rowItemLatestQueryRef.current[rowId] === normalizedQuery) {
          setRowItemSearchLoadingById((prev) => ({ ...prev, [rowId]: false }));
        }
      }
    }, 320);
  }, [allergySearchParam, resolvedClientId]);

  const handleRowItemSelect = React.useCallback(
    (mealName: string, rowIndex: number, rowId: string, selectedItem: FoodSearchResult) => {
      const blockingIssue = selectedItem.clinicalWarnings?.find((issue) => issue.severity === 'blocking');
      if (blockingIssue?.message) {
        showErrorToast(blockingIssue.message, 'Clinically blocked');
        return;
      }
      const firstWarning = selectedItem.clinicalWarnings?.find((issue) => issue.severity === 'warning');
      if (firstWarning?.message) {
        showWarningToast(firstWarning.message, 'Clinical warning', { duration: 7000 });
      }
      setRowsByMeal((prev) => {
        const rows = prev[mealName] ?? [];
        if (!rows[rowIndex]) return prev;

        const nextRows = [...rows];
        const existingRow = nextRows[rowIndex];
        const isExistingServerRow = Boolean(existingRow.itemId);
        const selectedIngredientUnits = Array.from(
          new Set(
            (selectedItem.possibleUnits ?? [])
              .map((unit) => String(unit ?? '').trim())
              .filter(Boolean),
          ),
        );
        const existingIngredientUnits = Array.from(
          new Set(
            (existingRow.possibleUnits ?? [])
              .map((unit) => String(unit ?? '').trim())
              .filter(Boolean),
          ),
        );
        const ingredientUnits = Array.from(
          new Set([...selectedIngredientUnits, ...existingIngredientUnits]),
        );
        const normalizeServingOverrides = (
          overrides: FoodSearchResult['servingOverrides'],
        ): NonNullable<ExpandedFoodRow['servingOverrides']> => {
          const deduped = new Map<string, NonNullable<ExpandedFoodRow['servingOverrides']>[number]>();
          (overrides ?? []).forEach((entry) => {
            const servingId = String(entry?.servingId ?? '').trim();
            if (!servingId.length) return;
            const key = servingId.toLowerCase();
            if (deduped.has(key)) return;
            const grams = Number(entry?.grams);
            deduped.set(key, {
              servingId,
              ...(Number.isFinite(grams) ? { grams } : {}),
            });
          });
          return Array.from(deduped.values());
        };
        const selectedIngredientServingOverrides = normalizeServingOverrides(selectedItem.servingOverrides);
        const existingIngredientServingOverrides = normalizeServingOverrides(existingRow.servingOverrides);
        const ingredientServingOverrides = normalizeServingOverrides([
          ...selectedIngredientServingOverrides,
          ...existingIngredientServingOverrides,
        ]);
        const preferredDefaultServingId =
          String(selectedItem.defaultServingId ?? '').trim()
          || String(existingRow.defaultServingId ?? '').trim()
          || undefined;
        const selectedDefaultServingGrams = Number(selectedItem.defaultServingGrams);
        const existingDefaultServingGrams = Number(existingRow.defaultServingGrams);
        const preferredDefaultServingGrams = Number.isFinite(selectedDefaultServingGrams)
          ? selectedDefaultServingGrams
          : Number.isFinite(existingDefaultServingGrams)
            ? existingDefaultServingGrams
            : undefined;
        const preferredIngredientUnit =
          existingRow.unit?.trim()
          || preferredDefaultServingId
          || ingredientUnits[0]
          || '';
        nextRows[rowIndex] = {
          ...existingRow,
          item: selectedItem.name,
          ...(isExistingServerRow
            ? {}
            : {
                itemType: selectedItem.type,
                source: resolveSearchResultProvider(selectedItem),
                sourceId: selectedItem.sourceId ?? selectedItem.id,
                ...(selectedItem.type === 'ingredient'
                  ? {
                      unit: preferredIngredientUnit,
                      serving: existingRow.serving?.trim() || '',
                      possibleUnits: ingredientUnits.length > 0 ? ingredientUnits : undefined,
                      servingOverrides: ingredientServingOverrides.length > 0
                        ? ingredientServingOverrides
                        : undefined,
                      defaultServingId: preferredDefaultServingId,
                      defaultServingGrams: preferredDefaultServingGrams,
                    }
                  : {
                      unit: '',
                      serving: '',
                      possibleUnits: undefined,
                      servingOverrides: undefined,
                      defaultServingId: undefined,
                      defaultServingGrams: undefined,
                    }),
              }),
        };

        const apiMealName = mealMeta.find((m) => m.name === mealName)?.apiMealName ?? mealName;
        const rowsForMeal = ensureDefaultEmptyRowsForMeal(apiMealName, nextRows);

        const nextState = { ...prev, [mealName]: rowsForMeal };
        rowsByMealRef.current = nextState;
        return nextState;
      });

      setRowItemSearchOpenById((prev) => ({ ...prev, [rowId]: false }));
    },
    [mealMeta],
  );

  const updateRowField = React.useCallback(
    (mealName: string, rowIndex: number, field: keyof ExpandedFoodRow, value: string | number) => {
      if (field === 'qty') return;

      const rows = rowsByMealRef.current[mealName] ?? [];
      const existingRow = rows[rowIndex];
      if (!existingRow) return;

      const ensuredRowId = existingRow.rowId ?? resolveRowId(mealName, rowIndex, existingRow.itemId, existingRow.item);
      const syncField: QuantitySyncField | null =
        field === 'unit' || field === 'serving' ? field : null;
      if (syncField && isRowQuantitySyncing(mealName, ensuredRowId)) {
        return;
      }

      let targetRowId: string | null = null;
      setRowsByMeal((prev) => {
        const mealRows = prev[mealName] ?? [];
        if (!mealRows[rowIndex]) return prev;

        const nextRows = [...mealRows];
        const currentRow = nextRows[rowIndex];
        const resolvedRowId = currentRow.rowId ?? resolveRowId(mealName, rowIndex, currentRow.itemId, currentRow.item);
        nextRows[rowIndex] = { ...currentRow, rowId: resolvedRowId, [field]: String(value) };
        targetRowId = resolvedRowId;

        const nextState = { ...prev, [mealName]: nextRows };
        rowsByMealRef.current = nextState;
        return nextState;
      });

      if (targetRowId && syncField) {
        queueRowQuantitySync(mealName, targetRowId, syncField);
      }
    },
    [isRowQuantitySyncing, queueRowQuantitySync],
  );

  const commitRowQty = React.useCallback(
    (mealName: string, rowId: string) => {
      const rowKey = getQuantityRowKey(mealName, rowId);
      if (isRowQuantitySyncing(mealName, rowId)) {
        if (qtyDraftCommitTimersRef.current[rowKey]) {
          clearTimeout(qtyDraftCommitTimersRef.current[rowKey]);
        }
        qtyDraftCommitTimersRef.current[rowKey] = setTimeout(() => {
          delete qtyDraftCommitTimersRef.current[rowKey];
          commitRowQty(mealName, rowId);
        }, 250);
        return;
      }

      const rows = rowsByMealRef.current[mealName] ?? [];
      const rowIndex = rows.findIndex((row, index) => {
        const candidateId = row.rowId ?? resolveRowId(mealName, index, row.itemId, row.item);
        return candidateId === rowId;
      });
      const existingRow = rowIndex >= 0 ? rows[rowIndex] : undefined;
      if (!existingRow) return;

      const rawInput = qtyDraftByRowKeyRef.current[rowKey];
      if (rawInput == null) return;

      const clearDraft = () => {
        delete qtyDraftByRowKeyRef.current[rowKey];
        setQtyDraftByRowKey((prev) => {
          if (!(rowKey in prev)) return prev;
          const next = { ...prev };
          delete next[rowKey];
          return next;
        });
      };

      if (rawInput.trim() === '') {
        clearDraft();
        return;
      }

      const parsed = Number.parseFloat(rawInput);
      if (!Number.isFinite(parsed) || parsed < 0) {
        clearDraft();
        return;
      }

      const nextQty = Math.max(0.1, Math.round(parsed * 10) / 10);
      const currentQty = Math.max(0.1, Math.round((Number(existingRow.qty) || 0) * 10) / 10);

      if (nextQty === currentQty) {
        clearDraft();
        return;
      }

      setRowsByMeal((prev) => {
        const mealRows = prev[mealName] ?? [];
        if (!mealRows[rowIndex]) return prev;

        const nextRows = [...mealRows];
        const currentRow = nextRows[rowIndex];
        const resolvedRowId = currentRow.rowId ?? resolveRowId(mealName, rowIndex, currentRow.itemId, currentRow.item);
        nextRows[rowIndex] = { ...currentRow, rowId: resolvedRowId, qty: nextQty };

        const nextState = { ...prev, [mealName]: nextRows };
        rowsByMealRef.current = nextState;
        return nextState;
      });

      clearDraft();
      void syncRowQuantity(mealName, rowId, 'qty', nextQty);
    },
    [getQuantityRowKey, isRowQuantitySyncing, syncRowQuantity],
  );

  const scheduleQtyDraftCommit = React.useCallback(
    (mealName: string, rowId: string) => {
      const rowKey = getQuantityRowKey(mealName, rowId);
      if (qtyDraftCommitTimersRef.current[rowKey]) {
        clearTimeout(qtyDraftCommitTimersRef.current[rowKey]);
      }
      qtyDraftCommitTimersRef.current[rowKey] = setTimeout(() => {
        delete qtyDraftCommitTimersRef.current[rowKey];
        commitRowQty(mealName, rowId);
      }, quantitySyncDelayMs);
    },
    [commitRowQty, getQuantityRowKey],
  );

  const handleQtyDraftChange = React.useCallback(
    (mealName: string, rowIndex: number, rawValue: string) => {
      const rows = rowsByMealRef.current[mealName] ?? [];
      const existingRow = rows[rowIndex];
      if (!existingRow) return;

      const rowId = existingRow.rowId ?? resolveRowId(mealName, rowIndex, existingRow.itemId, existingRow.item);
      if (rawValue !== '' && !/^\d*\.?\d*$/.test(rawValue)) return;

      const rowKey = getQuantityRowKey(mealName, rowId);
      qtyDraftByRowKeyRef.current[rowKey] = rawValue;
      setQtyDraftByRowKey((prev) => ({ ...prev, [rowKey]: rawValue }));
      scheduleQtyDraftCommit(mealName, rowId);
    },
    [getQuantityRowKey, scheduleQtyDraftCommit],
  );

  const adjustRowQty = React.useCallback(
    (mealName: string, rowIndex: number, delta: number) => {
      const rows = rowsByMealRef.current[mealName] ?? [];
      const existingRow = rows[rowIndex];
      if (!existingRow) return;

      const ensuredRowId = existingRow.rowId ?? resolveRowId(mealName, rowIndex, existingRow.itemId, existingRow.item);
      if (isRowQuantitySyncing(mealName, ensuredRowId)) {
        return;
      }

      const rowKey = getQuantityRowKey(mealName, ensuredRowId);
      delete qtyDraftByRowKeyRef.current[rowKey];
      setQtyDraftByRowKey((prev) => {
        if (!(rowKey in prev)) return prev;
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });

      let targetRowId: string | null = null;
      setRowsByMeal((prev) => {
        const mealRows = prev[mealName] ?? [];
        if (!mealRows[rowIndex]) return prev;

        const nextRows = [...mealRows];
        const currentRow = nextRows[rowIndex];
        const resolvedRowId = currentRow.rowId ?? resolveRowId(mealName, rowIndex, currentRow.itemId, currentRow.item);
        const currentQty = Number(currentRow.qty) || 0;
        const nextQty = Math.max(0.1, Math.round((currentQty + delta) * 10) / 10);
        nextRows[rowIndex] = { ...currentRow, rowId: resolvedRowId, qty: nextQty };
        targetRowId = resolvedRowId;
        const nextState = { ...prev, [mealName]: nextRows };
        rowsByMealRef.current = nextState;
        return nextState;
      });

      if (targetRowId) {
        queueRowQuantitySync(mealName, targetRowId, 'qty');
      }
    },
    [getQuantityRowKey, isRowQuantitySyncing, queueRowQuantitySync],
  );

  const openAddItemModal = React.useCallback(
    (
      mealName: string,
      rowContext?: { rowIndex: number; rowSearchId: string; type: 'food' | 'ingredient' },
      anchorEl?: HTMLElement | null,
      genericOptionContext?: GenericMealOptionAddContext,
    ) => {
      if (Date.now() < modalReopenBlockedUntilRef.current) {
        return;
      }
      setAddItemMealName(mealName);
      setAddItemAnchorEl(anchorEl ?? null);
      setAddItemSearchValue('');
      setAddItemSearchResults([]);
      setAddItemSearchFilters(EMPTY_ADD_ITEM_SEARCH_FILTERS);
      setIsAddItemSearching(false);
      setAddItemGenericOptionContext(genericOptionContext ?? null);
      const resolvedRowContext = rowContext
        ? rowContext
        : genericOptionContext
          ? null
          : resolveEmptyRowSelectionContext(mealName, rowsByMealRef.current[mealName] ?? []);
      setActiveRowSelectionContext(
        resolvedRowContext
          ? {
              mealName,
              rowIndex: resolvedRowContext.rowIndex,
              rowSearchId: resolvedRowContext.rowSearchId,
              type: resolvedRowContext.type,
            }
          : null,
      );
      setIsAddItemModalOpen(true);
    },
    [],
  );

  const closeAddItemModal = React.useCallback(() => {
    modalReopenBlockedUntilRef.current = Date.now() + 300;
    setIsAddItemModalOpen(false);
    setAddItemMealName(null);
    setAddItemAnchorEl(null);
    setAddItemSearchValue('');
    setAddItemSearchResults([]);
    setAddItemSearchFilters(EMPTY_ADD_ITEM_SEARCH_FILTERS);
    setIsAddItemSearching(false);
    setAddItemGenericOptionContext(null);
    setActiveRowSelectionContext(null);
  }, []);

  const addItemOptionNames = React.useMemo(
    () => Array.from(new Set(enrichedAddItemSearchResults.map(getFoodSearchResultOptionKey).filter(Boolean))),
    [enrichedAddItemSearchResults],
  );
  const addItemCurrentIntolerances = React.useMemo(() => {
    const fromPlan = appliedIntolerances.map((value) => String(value ?? '').trim()).filter(Boolean);
    if (fromPlan.length > 0) {
      return Array.from(new Set(fromPlan));
    }
    const fromClient = Array.isArray(clientDetails?.foodIntolerances)
      ? clientDetails.foodIntolerances.map((value) => String(value ?? '').trim()).filter(Boolean)
      : [];
    return Array.from(new Set(fromClient));
  }, [appliedIntolerances, clientDetails?.foodIntolerances]);

  const findEmptyRowSearchAnchor = React.useCallback(
    (mealName: string, type: 'food' | 'ingredient'): HTMLElement | null => {
      if (typeof document === 'undefined') return null;
      const desiredRowType = type === 'ingredient' ? 'ingredient' : 'food';
      const anchors = Array.from(document.querySelectorAll<HTMLElement>('[data-row-search-anchor="true"]'));
      return (
        anchors.find(
          (anchor) =>
            anchor.dataset.mealName === mealName
            && anchor.dataset.rowType === desiredRowType
            && anchor.dataset.rowEmpty === 'true',
        ) ?? null
      );
    },
    [],
  );

  const handleInlineFoodItemSelect = React.useCallback(async (
    mealName: string,
    selectedItem: FoodSearchResult,
    options?: { genericOptionTarget?: GenericOptionAddTarget; addingRowKey?: string },
  ) => {
    const normalizedFoodName = String(selectedItem.name ?? '').trim();
    if (!normalizedFoodName) return;
    const provider = resolveSearchResultProvider(selectedItem);
    const source = selectedItem.source === 'cached' ? 'cached' : 'api';
    const preferredIngredientServingId =
      selectedItem.type === 'ingredient'
        ? resolvePreferredIngredientServingId(selectedItem)
        : undefined;
    const selectedId = resolveSearchResultAddId(selectedItem);

    if (!resolvedPlanId) {
      showWarningToast('Plan unavailable', 'Please open a saved meal plan before adding food items.');
      return;
    }

    const addingRowKey = options?.addingRowKey ?? buildAddingItemRowKey(mealName);
    beginInlineSave();
    beginAddingItem(addingRowKey, normalizedFoodName);
    try {
      if (isAIDraftFlow) {
        await runAIDraftFoodSelection({
          resolvedPlanId,
          selectedDayNumber,
          mealName,
          selectedItem,
          selectedId,
          provider,
          source,
          isDraftGenerationPending,
          resolveMealApiName,
          defaultAddIngredientServingId,
          defaultAddRecipeServingId,
          preferredIngredientServingId,
        });
      } else {
        const isAIFinalizedIngredientSelection =
          isAIPlanType && !isAIDraftFlow && selectedItem.type === 'ingredient';
        const { skippedWithWarning } = isAIFinalizedIngredientSelection
          ? await runManualOrFinalizedIngredientSelection({
            resolvedPlanId,
            selectedDayNumber,
            mealName,
            planStatus: effectivePlanStatus,
            normalizedIngredientName: normalizedFoodName,
            selectedId,
            provider,
            resolveMealApiName,
            finalizedMealId: mealMeta.find((meal) => meal.name === mealName)?.mealId,
            insideRecipe: false,
            rowsByMeal: rowsByMealRef.current,
            isIngredientRow,
            defaultAddIngredientServingId,
            preferredIngredientServingId,
          })
          : await runManualOrFinalizedFoodSelection({
            resolvedPlanId,
            selectedDayNumber,
            mealName,
            planStatus: effectivePlanStatus,
            selectedItem,
            normalizedFoodName,
            selectedId,
            provider,
            source,
            resolveMealApiName,
            resolveFinalizedMealType,
            finalizedMealId: mealMeta.find((meal) => meal.name === mealName)?.mealId,
            rowsByMeal: rowsByMealRef.current,
            isIngredientRow,
            defaultAddIngredientServingId,
            defaultAddRecipeServingId,
            preferredIngredientServingId,
            genericOptionTarget: options?.genericOptionTarget,
          });
        if (skippedWithWarning) {
          return;
        }
      }

      await loadDayData();
      refreshClinicalInsights();
    } catch (error) {
      showErrorToast(error, 'Unable to add food item');
    } finally {
      endAddingItem(addingRowKey);
      endInlineSave();
    }
  }, [
    beginAddingItem,
    beginInlineSave,
    endAddingItem,
    endInlineSave,
    isAIDraftFlow,
    isDraftGenerationPending,
    effectivePlanStatus,
    isAIPlanType,
    loadDayData,
    mealMeta,
    refreshClinicalInsights,
    resolveMealApiName,
    resolvedPlanId,
    selectedDayNumber,
  ]);

  const handleInlineIngredientSelect = React.useCallback(async (
    mealName: string,
    selectedItem: FoodSearchResult,
    options?: { insideRecipe?: boolean; genericOptionTarget?: GenericOptionAddTarget; addingRowKey?: string },
  ) => {
    const normalizedIngredientName = String(selectedItem.name ?? '').trim();
    if (!normalizedIngredientName) return;
    const provider = resolveSearchResultProvider(selectedItem);
    const preferredIngredientServingId = resolvePreferredIngredientServingId(selectedItem);
    const selectedId = String(selectedItem.id ?? '').trim();

    if (!resolvedPlanId) {
      showWarningToast('Plan unavailable', 'Please open a saved meal plan before adding ingredients.');
      return;
    }

    const addingRowKey = options?.addingRowKey ?? buildAddingItemRowKey(mealName);
    beginInlineSave();
    beginAddingItem(addingRowKey, normalizedIngredientName);
    try {
      if (isAIDraftFlow) {
        await runAIDraftIngredientSelection({
          resolvedPlanId,
          selectedDayNumber,
          mealName,
          isDraftGenerationPending,
          selectedId,
          provider,
          resolveMealApiName,
          defaultAddIngredientServingId,
          preferredIngredientServingId,
        });
      } else {
        const { skippedWithWarning } = await runManualOrFinalizedIngredientSelection({
          resolvedPlanId,
          selectedDayNumber,
          mealName,
          planStatus: effectivePlanStatus,
          normalizedIngredientName,
          selectedId,
          provider,
          resolveMealApiName,
          finalizedMealId: mealMeta.find((meal) => meal.name === mealName)?.mealId,
          insideRecipe: isAIPlanType ? false : (options?.insideRecipe ?? false),
          rowsByMeal: rowsByMealRef.current,
          isIngredientRow,
          defaultAddIngredientServingId,
          preferredIngredientServingId,
          genericOptionTarget: options?.genericOptionTarget,
        });
        if (skippedWithWarning) {
          return;
        }
      }

      await loadDayData();
      refreshClinicalInsights();
    } catch (error) {
      showErrorToast(error, 'Unable to add ingredient');
    } finally {
      endAddingItem(addingRowKey);
      endInlineSave();
    }
  }, [
    beginAddingItem,
    beginInlineSave,
    endAddingItem,
    endInlineSave,
    isAIDraftFlow,
    isDraftGenerationPending,
    effectivePlanStatus,
    loadDayData,
    refreshClinicalInsights,
    resolveMealApiName,
    mealMeta,
    resolvedPlanId,
    selectedDayNumber,
  ]);

  const openImportRecipesModal = React.useCallback((mealName: string, optionContext?: GenericMealOptionAddContext) => {
    setImportRecipesMealName(mealName);
    setImportRecipesOptionContext(optionContext ?? null);
    setIsImportRecipesOpen(true);
  }, []);

  const closeImportRecipesModal = React.useCallback(() => {
    setIsImportRecipesOpen(false);
    setImportRecipesMealName(null);
    setImportRecipesOptionContext(null);
  }, []);

  const openSaveTemplateModal = React.useCallback(() => {
    setTemplateSaveTarget({ kind: 'meal_plan' });
    setIsSaveTemplateOpen(true);
  }, []);

  const openMealSlotTemplateModal = React.useCallback((mealName: string) => {
    setTemplateSaveTarget({ kind: 'meal_slot', mealName });
    setIsSaveTemplateOpen(true);
  }, []);

  const closeSaveTemplateModal = React.useCallback(() => {
    setIsSaveTemplateOpen(false);
    setTemplateSaveTarget(null);
  }, []);

  const handleSaveTemplate = React.useCallback(
    async (payload: SaveAsTemplatePayload) => {
      const target = templateSaveTarget ?? { kind: 'meal_plan' as const };
      const isDraftPlan = effectivePlanStatus === 'draft' || effectivePlanStatus === 'generic';

      trackMealPlanTemplateSaveClicked({
        product_area: 'meal_plans',
        meal_plan_surface: 'workspace',
        template_target: target.kind,
        plan_status: effectivePlanStatus,
      });

      try {
        if (!resolvedPlanId) {
          showWarningToast('Plan unavailable', 'Please open a saved meal plan before saving a template.');
          throw new Error('Plan unavailable');
        }

        if (target.kind === 'meal_plan') {
          const daySlots = [...mealMeta];
          const slotHasRecipes = (mealName: string) =>
            (rowsByMeal[mealName] ?? []).some(isPickableNutritionRow);
          const filledSlots = daySlots.filter((meal) => slotHasRecipes(meal.name));

          if (daySlots.length === 0 || filledSlots.length === 0) {
            showWarningToast('Empty meal plan', 'Cannot save an empty meal plan as a template.');
            throw new Error('Empty meal plan');
          }
          if (filledSlots.length < daySlots.length) {
            showWarningToast(
              'Incomplete meal plan',
              'Please add recipes to all required meal slots before saving this template.',
            );
            throw new Error('Incomplete meal plan');
          }

          const response = isDraftPlan
            ? await saveDraftMealPlanAsTemplate(resolvedPlanId, {
                templateName: payload.templateName,
                description: payload.description,
                suitableFor: payload.suitableFor,
                tags: payload.clinicalTags,
                visibility: payload.visibility,
                dayNumber: selectedDayNumber,
              })
            : await saveFinalizedMealPlanAsTemplate(resolvedPlanId, {
                templateName: payload.templateName,
                description: payload.description,
                suitableFor: payload.suitableFor,
                tags: payload.clinicalTags,
                visibility: payload.visibility,
                dayNumber: selectedDayNumber,
              });

          if (!response?.success) {
            throw new Error(response?.message || 'Unable to save meal plan template');
          }
          showSuccessToast('Meal plan saved as template');
          return;
        }

        const selectedMeal = mealMeta.find((meal) => meal.name === target.mealName);
        if (!selectedMeal) {
          showWarningToast('Meal slot unavailable', 'Please select an available meal slot.');
          throw new Error('Meal slot unavailable');
        }

        if (!(rowsByMeal[target.mealName] ?? []).some(isPickableNutritionRow)) {
          showWarningToast(
            'Empty meal slot',
            'Please add recipes to this meal slot before saving it as a template.',
          );
          throw new Error('Empty meal slot');
        }

        const slotIdentifier = buildMealSlotTemplateIdentifier(selectedMeal, isDraftPlan);
        if (!Object.keys(slotIdentifier).length) {
          showWarningToast('Meal slot unavailable', 'This meal slot is missing an identifier.');
          throw new Error('Meal slot identifier unavailable');
        }

        const requestBody: SaveMealSlotTemplateRequest = {
          ...slotIdentifier,
          day: selectedDayNumber,
          dayNumber: selectedDayNumber,
          templateName: payload.templateName,
          description: payload.description,
          suitableFor: payload.suitableFor,
          clinicalTags: payload.clinicalTags,
          visibility: payload.visibility,
        };

        const response = isDraftPlan
          ? await saveDraftMealSlotAsTemplate(resolvedPlanId, requestBody)
          : await saveFinalizedMealSlotAsTemplate(resolvedPlanId, requestBody);

        if (!response?.success) {
          throw new Error(response?.message || 'Unable to save meal slot template');
        }
        showSuccessToast('Meal slot saved as template');
      } catch (error) {
        const localValidationMessages = new Set([
          'Plan unavailable',
          'Empty meal plan',
          'Incomplete meal plan',
          'Meal slot unavailable',
          'Empty meal slot',
          'Meal slot identifier unavailable',
        ]);
        const message = error instanceof Error ? error.message : '';
        if (!localValidationMessages.has(message)) {
          showErrorToast(
            error,
            target.kind === 'meal_slot' ? 'Unable to save meal slot template' : 'Unable to save meal plan template',
          );
        }
        throw error;
      }
    },
    [effectivePlanStatus, mealMeta, resolvedPlanId, rowsByMeal, selectedDayNumber, templateSaveTarget],
  );

  const hiddenImportedRecipeIds = React.useMemo(() => {
    if (!importRecipesMealName) return [];
    const rows = rowsByMeal[importRecipesMealName] ?? [];
    return Array.from(
      new Set(
        rows
          .filter((row) => row.itemType === 'recipe')
          .flatMap((row) => [row.sourceId, row.recipeId])
          .map((value) => String(value ?? '').trim())
          .filter(Boolean),
      ),
    );
  }, [importRecipesMealName, rowsByMeal]);

  const handleImportRecipes = React.useCallback(
    async (selectedRecipes: RecipeLibraryItem[]) => {
      if (!importRecipesMealName || !selectedRecipes.length) return;

      if (!resolvedPlanId) {
        showWarningToast('Import unavailable', 'Please open a saved meal plan before importing recipes.');
        return;
      }

      setImportingRecipesMealName(importRecipesMealName);
      try {
        const apiMealName = resolveMealApiName(importRecipesMealName);
        let genericOptionTarget: GenericOptionAddTarget | null = null;
        let genericSelectedIndex: number | null = null;

        if (isGenericPlan && importRecipesOptionContext) {
          const rows = rowsByMealRef.current[importRecipesMealName] ?? [];
          const firstRowIndex =
            typeof importRecipesOptionContext.firstRowIndex === 'number'
              ? importRecipesOptionContext.firstRowIndex
              : null;
          const firstRow = firstRowIndex !== null ? rows[firstRowIndex] : undefined;
          const existingGroupId = String(importRecipesOptionContext.optionGroupId ?? firstRow?.optionGroupId ?? '').trim();
          const optionGroupId = existingGroupId || buildGenericOptionGroupId(importRecipesMealName);
          const optionGroupLabel = String(importRecipesOptionContext.optionGroupLabel ?? '').trim() || 'Meal choice';
          const pickableIndices = rows
            .map((row, index) => (isPickableNutritionRow(row) ? index : -1))
            .filter((index) => index >= 0);
          const currentChoice = genericSlotCheckboxChoice[importRecipesMealName];
          const effectiveCurrentChoice =
            typeof currentChoice === 'number' && pickableIndices.includes(currentChoice)
              ? currentChoice
              : pickableIndices[0] ?? null;
          const currentChoiceRow = effectiveCurrentChoice !== null ? rows[effectiveCurrentChoice] : undefined;
          const currentChoiceGroupId = String(currentChoiceRow?.optionGroupId ?? '').trim();
          const targetIsCurrentChoice = Boolean(
            firstRow
            && effectiveCurrentChoice !== null
            && (
              effectiveCurrentChoice === firstRowIndex
              || (
                existingGroupId
                && String(currentChoiceRow?.optionGroupId ?? '').trim() === existingGroupId
              )
            ),
          );
          const selectedOptionGroupId = targetIsCurrentChoice || effectiveCurrentChoice === null
            ? optionGroupId
            : currentChoiceGroupId || null;

          if (!existingGroupId && firstRow && isPickableNutritionRow(firstRow)) {
            const firstItemId = resolveGenericOptionRowItemId(firstRow);
            if (!firstItemId) {
              showWarningToast('Item unavailable', 'Unable to identify the first item in this meal choice.');
              return;
            }
            const response = isFinalizedMealPlanStatus(planStatus)
              ? await updateFinalizedMealPlanItem(resolvedPlanId, {
                  itemId: firstItemId,
                  optionGroupId,
                  optionGroupLabel,
                  selectedOptionGroupId,
                })
              : await updateGenericMealPlanItem({
                  genericPlanId: resolvedPlanId,
                  mealName: apiMealName,
                  itemId: firstItemId,
                  optionGroupId,
                  optionGroupLabel,
                  selectedOptionGroupId,
                });
            if (!response.success) {
              throw new Error(response.message || 'Unable to prepare this meal choice');
            }
          }

          genericOptionTarget = {
            optionGroupId,
            optionGroupLabel,
            selectedOptionGroupId,
          };
          genericSelectedIndex =
            targetIsCurrentChoice || effectiveCurrentChoice === null
              ? (firstRowIndex !== null ? firstRowIndex : rows.length)
              : effectiveCurrentChoice;
        }

        const recipes = selectedRecipes.map((recipe) => ({
          id: String(recipe.id),
          provider: recipe.provider ?? 'spoonacular',
          source: recipe.source ?? 'api',
          nutritionServings: recipe.nutritionServings ?? 1,
          ...(genericOptionTarget
            ? {
                optionGroupId: genericOptionTarget.optionGroupId,
                optionGroupLabel: genericOptionTarget.optionGroupLabel,
                selectedOptionGroupId: genericOptionTarget.selectedOptionGroupId,
              }
            : {}),
        }));
        const response = isGenericPlan
          ? await addRecipesToGenericMealPlan({
              genericPlanId: resolvedPlanId,
              mealName: apiMealName,
              recipes,
            })
          : await addRecipesToManualMealPlan({
              draftId: resolvedPlanId,
              day: selectedDayNumber,
              mealName: apiMealName,
              recipes,
            });

        if (!response.success) {
          throw new Error(response.message || 'Failed to import recipes');
        }
        if (isGenericPlan && genericSelectedIndex !== null) {
          setGenericSlotCheckboxChoice((prev) => ({ ...prev, [importRecipesMealName]: genericSelectedIndex }));
        }
        await loadDayData();
        refreshClinicalInsights();
      } catch (error) {
        showErrorToast(error, 'Unable to import recipes');
        throw error instanceof Error ? error : new Error('Unable to import recipes');
      } finally {
        setImportingRecipesMealName((current) => (current === importRecipesMealName ? null : current));
      }
    },
    [
      genericSlotCheckboxChoice,
      importRecipesMealName,
      importRecipesOptionContext,
      isGenericPlan,
      loadDayData,
      planStatus,
      refreshClinicalInsights,
      resolveMealApiName,
      resolvedPlanId,
      selectedDayNumber,
    ],
  );

  const handleNutritionRowCheckboxChange = React.useCallback(
    (mealName: string, rowIndex: number, checked: boolean) => {
      if (isGenericOriginPlan) {
        const previousChoice = genericSlotCheckboxChoice[mealName] ?? null;
        setGenericSlotCheckboxChoice((prev) => ({
          ...prev,
          [mealName]: checked ? rowIndex : prev[mealName] === rowIndex ? null : prev[mealName],
        }));
        if (!checked || !resolvedPlanId) return;

        const row = rowsByMealRef.current[mealName]?.[rowIndex];
        const itemId = row ? resolveGenericOptionRowItemId(row) : '';
        const selectedOptionGroupId = String(row?.optionGroupId ?? '').trim() || null;
        if (!itemId) {
          setGenericSlotCheckboxChoice((prev) => ({
            ...prev,
            [mealName]: previousChoice,
          }));
          showWarningToast('Item unavailable', 'Unable to identify the selected option item.');
          return;
        }

        void (async () => {
          try {
            if (isFinalizedMealPlanStatus(planStatus)) {
              const response = await updateFinalizedMealPlanItem(resolvedPlanId, {
                itemId,
                selectedOptionGroupId,
              });
              if (!response.success) {
                throw new Error(response.message || 'Unable to update meal choice');
              }
            } else {
              const response = await updateGenericMealPlanItem({
                genericPlanId: resolvedPlanId,
                mealName: resolveMealApiName(mealName),
                itemId,
                selectedOptionGroupId,
              });
              if (!response.success) {
                throw new Error(response.message || 'Unable to update meal choice');
              }
            }

            await loadDayData();
            refreshClinicalInsights();
            await onMealPlanUpdated?.();
          } catch (error) {
            setGenericSlotCheckboxChoice((prev) => ({
              ...prev,
              [mealName]: previousChoice,
            }));
            showErrorToast(error, 'Unable to update meal choice');
            await loadDayData().catch(() => undefined);
          }
        })();
        return;
      }
      setMealRowCheckboxState((prev) => ({
        ...prev,
        [mealName]: {
          ...(prev[mealName] ?? {}),
          [rowIndex]: checked,
        },
      }));
    },
    [
      genericSlotCheckboxChoice,
      isGenericOriginPlan,
      loadDayData,
      onMealPlanUpdated,
      planStatus,
      refreshClinicalInsights,
      resolveMealApiName,
      resolvedPlanId,
    ],
  );

  const handleAddGenericOptionItem = React.useCallback(
    async (mealName: string, optionContext: GenericMealOptionAddContext, selectedItem: FoodSearchResult) => {
      if (!isGenericOriginPlan) return;
      if (!resolvedPlanId) {
        showWarningToast('Plan unavailable', 'Please open a saved cohort meal plan before adding option items.');
        return;
      }

      const rows = rowsByMealRef.current[mealName] ?? [];
      const firstRowIndex = typeof optionContext.firstRowIndex === 'number' ? optionContext.firstRowIndex : null;
      const firstRow = firstRowIndex !== null ? rows[firstRowIndex] : undefined;
      const existingGroupId = String(optionContext.optionGroupId ?? firstRow?.optionGroupId ?? '').trim();
      const optionGroupId = existingGroupId || buildGenericOptionGroupId(mealName);
      const optionGroupLabel = String(optionContext.optionGroupLabel ?? '').trim() || 'Meal choice';
      const pickableIndices = rows
        .map((row, index) => (isPickableNutritionRow(row) ? index : -1))
        .filter((index) => index >= 0);
      const currentChoice = genericSlotCheckboxChoice[mealName];
      const effectiveCurrentChoice =
        typeof currentChoice === 'number' && pickableIndices.includes(currentChoice)
          ? currentChoice
          : pickableIndices[0] ?? null;
      const currentChoiceRow = effectiveCurrentChoice !== null ? rows[effectiveCurrentChoice] : undefined;
      const currentChoiceGroupId = String(currentChoiceRow?.optionGroupId ?? '').trim();
      const targetIsCurrentChoice = Boolean(
        firstRow
        && effectiveCurrentChoice !== null
        && (
          effectiveCurrentChoice === firstRowIndex
          || (
            existingGroupId
            && String(currentChoiceRow?.optionGroupId ?? '').trim() === existingGroupId
          )
        ),
      );
      const selectedOptionGroupId = targetIsCurrentChoice || effectiveCurrentChoice === null
        ? optionGroupId
        : currentChoiceGroupId || null;
      const genericOptionTarget: GenericOptionAddTarget = {
        optionGroupId,
        optionGroupLabel,
        selectedOptionGroupId,
      };

      setAddingGenericOptionMealName(mealName);
      try {
        const targetMealName = resolveMealApiName(mealName);
        if (isGenericPlan && !existingGroupId && firstRow && isPickableNutritionRow(firstRow)) {
          const firstItemId = resolveGenericOptionRowItemId(firstRow);
          if (!firstItemId) {
            showWarningToast('Item unavailable', 'Unable to identify the first item in this option.');
            return;
          }
          const response = isFinalizedMealPlanStatus(planStatus)
            ? await updateFinalizedMealPlanItem(resolvedPlanId, {
                itemId: firstItemId,
                optionGroupId,
                optionGroupLabel,
                selectedOptionGroupId,
              })
            : await updateGenericMealPlanItem({
                genericPlanId: resolvedPlanId,
                mealName: targetMealName,
                itemId: firstItemId,
                optionGroupId,
                optionGroupLabel,
                selectedOptionGroupId,
              });
          if (!response.success) {
            throw new Error(response.message || 'Unable to prepare this option');
          }
        }

        if (selectedItem.type === 'ingredient') {
          const emptyContext = resolveEmptyRowSelectionContext(
            mealName,
            rowsByMealRef.current[mealName] ?? [],
            'ingredient',
          );
          await handleInlineIngredientSelect(mealName, selectedItem, {
            insideRecipe: false,
            genericOptionTarget,
            addingRowKey: buildAddingItemRowKey(mealName, emptyContext?.rowSearchId),
          });
        } else {
          const emptyContext = resolveEmptyRowSelectionContext(
            mealName,
            rowsByMealRef.current[mealName] ?? [],
            'food',
          );
          await handleInlineFoodItemSelect(mealName, selectedItem, {
            genericOptionTarget,
            addingRowKey: buildAddingItemRowKey(mealName, emptyContext?.rowSearchId),
          });
        }

        const selectedIndex =
          targetIsCurrentChoice || effectiveCurrentChoice === null
            ? (firstRowIndex !== null ? firstRowIndex : rows.length)
            : effectiveCurrentChoice;
        setGenericSlotCheckboxChoice((prev) => ({ ...prev, [mealName]: selectedIndex }));
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to add option item');
      } finally {
        setAddingGenericOptionMealName((current) => (current === mealName ? null : current));
      }
    },
    [
      genericSlotCheckboxChoice,
      handleInlineFoodItemSelect,
      handleInlineIngredientSelect,
      isGenericOriginPlan,
      isGenericPlan,
      onMealPlanUpdated,
      planStatus,
      resolveMealApiName,
      resolvedPlanId,
    ],
  );

  const handleAddItemFromModal = React.useCallback(
    async (pickedName: string) => {
      if (!addItemMealName) return;

      const normalized = String(pickedName ?? '').trim();
      if (!normalized) return;

      const matched =
        enrichedAddItemSearchResults.find((item) => getFoodSearchResultOptionKey(item) === normalized)
        ?? enrichedAddItemSearchResults.find((item) => item.name.toLowerCase() === normalized.toLowerCase())
        ?? null;

      if (!matched) {
        showWarningToast('Item not found', 'Select an item from the search suggestions.');
        return;
      }

      const targetMealName = activeRowSelectionContext?.mealName ?? addItemMealName;
      const preferredEmptyType = matched.type === 'ingredient' ? 'ingredient' : 'food';
      const fallbackEmptyContext = resolveEmptyRowSelectionContext(
        targetMealName,
        rowsByMealRef.current[targetMealName] ?? [],
        preferredEmptyType,
      );
      const addingRowKey = buildAddingItemRowKey(
        targetMealName,
        activeRowSelectionContext?.rowSearchId ?? fallbackEmptyContext?.rowSearchId,
      );

      if (isGenericOriginPlan && addItemGenericOptionContext) {
        await handleAddGenericOptionItem(targetMealName, addItemGenericOptionContext, matched);
        closeAddItemModal();
        return;
      }

      if (matched.type === 'ingredient') {
        await handleInlineIngredientSelect(targetMealName, matched, {
          insideRecipe: activeRowSelectionContext?.type === 'ingredient',
          addingRowKey,
        });
      } else {
        await handleInlineFoodItemSelect(targetMealName, matched, { addingRowKey });
      }

      closeAddItemModal();
    },
    [
      activeRowSelectionContext,
      addItemGenericOptionContext,
      addItemMealName,
      closeAddItemModal,
      enrichedAddItemSearchResults,
      handleAddGenericOptionItem,
      handleInlineFoodItemSelect,
      handleInlineIngredientSelect,
      isGenericOriginPlan,
    ],
  );

  const handleCreateCustomRecipeFromAddItem = React.useCallback(() => {
    const clientId = String(resolvedClientId ?? '').trim();
    const draftId = String(resolvedPlanId ?? '').trim();
    if (!clientId || !draftId || clientId === 'template-preview') return;

    const params = new URLSearchParams({
      clientId,
      draftId,
      day: String(selectedDayNumber),
      from: `${location.pathname}${location.search}`,
    });
    const mealName = String(addItemMealName ?? '').trim();
    if (mealName) params.set('mealName', mealName);

    closeAddItemModal();
    navigate(`/recipes/create?${params.toString()}`);
  }, [
    addItemMealName,
    closeAddItemModal,
    location.pathname,
    location.search,
    navigate,
    resolvedClientId,
    resolvedPlanId,
    selectedDayNumber,
  ]);

  const handleTemplatePickImport = React.useCallback(
    async (template: MealPlanTemplateSummary) => {
      if (!resolvedPlanId) {
        showWarningToast('Import unavailable', 'Please open a saved meal plan before importing templates.');
        return;
      }
      trackMealPlanTemplateImportClicked({
        product_area: 'meal_plans',
        meal_plan_surface: 'workspace',
      });
      try {
        const response = await importMealPlanTemplate(resolvedPlanId, {
          templateId: String(template.id),
          day: selectedDayNumber,
          templateDay: 1,
        });
        if (!response.success) {
          throw new Error(response.message || 'Unable to import template');
        }
        showSuccessToast('Template imported into this day');
        await loadDayData();
        refreshClinicalInsights();
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to import template');
      }
    },
    [clientDetails, loadDayData, onMealPlanUpdated, refreshClinicalInsights, resolvedPlanId, selectedDayNumber],
  );

  const handleGenericMealPlanImport = React.useCallback(
    async (plan: GenericMealPlanSummary) => {
      if (!resolvedPlanId) {
        showWarningToast('Import unavailable', 'Please open a saved cohort meal plan before importing.');
        return;
      }
      const sourceGenericPlanId = String(plan.id ?? '').trim();
      if (!sourceGenericPlanId) {
        showWarningToast('Import unavailable', 'Select a valid cohort meal plan.');
        return;
      }

      try {
        const response = await importExistingGenericMealPlan(resolvedPlanId, {
          sourceGenericPlanId,
        });
        if (!response.success) {
          throw new Error(response.message || 'Unable to import cohort meal plan');
        }
        showSuccessToast('Cohort meal plan imported', 'All meals and slots were replaced from the selected plan.');
        await loadDayData();
        refreshClinicalInsights();
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to import cohort meal plan');
        throw error instanceof Error ? error : new Error('Unable to import cohort meal plan');
      }
    },
    [loadDayData, onMealPlanUpdated, refreshClinicalInsights, resolvedPlanId],
  );

  const handleDeleteTemplate = React.useCallback(() => {
    if (!isTemplateEditor) return;
    const templateId = String(resolvedPlanId ?? '').trim();
    if (!templateId) {
      showWarningToast('Template unavailable', 'Please open a saved template before deleting.');
      return;
    }
    setDeleteTemplateConfirmOpen(true);
  }, [isTemplateEditor, resolvedPlanId]);

  const handleDeleteTemplateConfirmSubmit = React.useCallback(async () => {
    const templateId = String(resolvedPlanId ?? '').trim();
    if (!templateId) {
      showWarningToast('Template unavailable', 'Please open a saved template before deleting.');
      setDeleteTemplateConfirmOpen(false);
      return;
    }
    setIsDeletingTemplate(true);
    try {
      const response = await deleteMealPlanTemplate(templateId);
      if (!response.success) {
        throw new Error(response.message || 'Unable to delete template');
      }
      showSuccessToast('Template deleted');
      if (safeFromPath) {
        navigate(safeFromPath);
      } else {
        navigate('/templates');
      }
    } catch (error) {
      showErrorToast(error, 'Unable to delete template');
    } finally {
      setIsDeletingTemplate(false);
      setDeleteTemplateConfirmOpen(false);
    }
  }, [navigate, resolvedPlanId, safeFromPath]);

  const groupedManualFinalizeIssues = React.useMemo(() => {
    const blockingIssues = manualFinalizeValidation?.blockingIssues ?? [];
    const warnings = manualFinalizeValidation?.warnings ?? [];
    const isCurrentDayOrPlanWide = (issue: { dayNumber?: number }) => (
      issue.dayNumber == null || issue.dayNumber === selectedDayNumber
    );
    const visibleBlockingIssues = blockingIssues.filter(isCurrentDayOrPlanWide);
    const visibleWarnings = warnings.filter(isCurrentDayOrPlanWide);
    const otherDayBlockingIssues = blockingIssues.filter((issue) => !isCurrentDayOrPlanWide(issue));
    const otherDayWarnings = warnings.filter((issue) => !isCurrentDayOrPlanWide(issue));
    const otherDayNumbers = Array.from(new Set([
      ...otherDayBlockingIssues.map((issue) => issue.dayNumber),
      ...otherDayWarnings.map((issue) => issue.dayNumber),
    ].filter((dayNumber): dayNumber is number => dayNumber != null))).sort((a, b) => a - b);

    return {
      visibleBlockingIssues,
      visibleWarnings,
      otherDayBlockingIssues,
      otherDayWarnings,
      otherDayNumbers,
    };
  }, [manualFinalizeValidation, selectedDayNumber]);

  return (
    <Box
      sx={{
        position: 'relative',
        py: embedded ? 0 : 1,
        mt: embedded ? 0 : { lg: 1.5 },
        ml: embedded ? 0 : { lg: 0.5 },
        width: '100%',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          minHeight: embedded ? 'auto' : 'calc(100vh - 7.5rem)',
          height: embedded ? 'auto' : 'calc(100vh - 7.5rem)',
          overflow: embedded ? 'visible' : 'hidden',
          borderRadius: embedded ? '18px' : '24px',
          border: '1px solid #cae3ec',
          bgcolor: '#ffffff',
          p: embedded ? 1.2 : 1.8,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isDayDataLoading ? (
            <Box
            sx={{
              width: '100%',
              minHeight: embedded ? 360 : '100%',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <CircularProgress size={36} sx={{ color: '#20AEDC' }} />
          </Box>
        ) : dayDataLoadError ? (
          <Box
            sx={{
              width: '100%',
              minHeight: embedded ? 360 : '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 2, sm: 4 },
            }}
          >
            <Box
              role="alert"
              sx={{
                width: '100%',
                maxWidth: 520,
                border: '1px solid #F5C2C7',
                borderRadius: '16px',
                bgcolor: '#FFF8F8',
                p: { xs: 2.5, sm: 3 },
                textAlign: 'left',
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#B42318' }}>
                Unable to load cohort meal plan
              </Typography>
              <Typography sx={{ mt: 0.75, fontSize: '0.9rem', color: '#7A4B4B', lineHeight: 1.5 }}>
                {dayDataLoadError}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2.2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                      return;
                    }
                    if (safeFromPath) {
                      navigate(safeFromPath);
                      return;
                    }
                    navigate(detailsV2MealPlanPath);
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setDayDataReloadKey((key) => key + 1)}
                  sx={{ bgcolor: '#20AEDC', '&:hover': { bgcolor: '#1899C3' } }}
                >
                  Retry
                </Button>
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box
            ref={contentScrollRef}
            sx={{
              width: '100%',
              height: embedded ? 'auto' : '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflowY: embedded ? 'visible' : 'auto',
              overflowX: 'hidden',
            }}
          >
            <Box ref={daySwitcherSectionRef}>
              <NutritionalAnalysisTopBar
                selectedDayNumber={selectedDayNumber}
                availableDayNumbers={availableDayNumbers}
                dayNamesByNumber={dayNamesByNumber}
                onDayChange={(dayNumber) => setSelectedDayNumber(dayNumber)}
                showDaySelector={!isTemplateEditor && !isGenericPlan}
                highlightDaySelector={highlightDaySelector}
                dayDisplayLabel={isGenericPlan ? planName.trim() || undefined : undefined}
                backLabel={topBarBackLabel ?? 'Back to Day Plan'}
                onBackClick={() => {
                  if (onBack) {
                    onBack();
                    return;
                  }
                  if (safeFromPath) {
                    navigate(safeFromPath);
                    return;
                  }
                  navigate(detailsV2MealPlanPath);
                }}
                totalCaloriesLabel={formatCalories(totalIntake.calories)}
                onSaveChanges={() => {
                  void handleSaveChanges();
                }}
                showSaveButton={
                  !isGenericPlan && !templateInteractionDisabled && !hideSaveChangesForFinalizedMealPlan
                }
                saveChangesDisabled={isInlineSaving || isAISaveSimulating}
                saveChangesLabel={saveChangesLabel}
                dayNoteAction={
                  !isGenericPlan && (canEditMealPlanNotes || dayNotes?.trim()) ? (
                    <MealPlanNotesControl
                      value={dayNotes}
                      disabled={isSavingMealPlanNotes}
                      editable={canEditMealPlanNotes}
                      variant="icon"
                      iconLabel="Day note"
                      size="sm"
                      label="Day note"
                      emptyLabel="Add a note for this day"
                      helperText="Optional. Add guidance for this day only."
                      placeholder="Enter day notes"
                      onSave={async (notes) =>
                        handleSaveMealPlanNotes({ level: 'day', day: selectedDayNumber, notes })
                      }
                    />
                  ) : undefined
                }
                showExportPdf={canExportFullMealPlanPdf}
                isExportingPdf={isExportingPdf}
                exportPdfDisabled={!canExportFullMealPlanPdf}
                onExportPdf={() => {
                  void handleExportMealPlanPdf();
                }}
              />
            </Box>

            <Box
              sx={{
                mt: 2.2,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  xl: showAnalysisSidebar ? 'minmax(0, 1fr) minmax(320px, 360px)' : '1fr',
                },
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {showClientSummaryStrip && shouldUseClientContext ? (
                  <MealSuggestionsClientSummaryStrip
                    client={clientDetails}
                    clientId={resolvedClientId}
                    clinicalTags={summaryClinicalTags}
                  />
                ) : null}

                {!embedded
                && !isGenericPlan
                && !isGenericOriginPlan
                && !isTemplateEditor
                && !isAIPlanType
                && mealPlanChoiceSegments.length > 0 ? (
                  <Box
                    component="section"
                    sx={{
                      mb: 2,
                      borderRadius: '12px',
                      border: '1px solid #CFFAFE',
                      bgcolor: 'rgba(236, 254, 255, 0.7)',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#334155',
                        lineHeight: 1.55,
                      }}
                    >
                      <Box component="span">Your preferred choices for this meal plan - </Box>
                      {mealPlanChoiceSegments.map((segment, index) => (
                        <React.Fragment key={segment.label}>
                          {index > 0 ? <Box component="span">; </Box> : null}
                          <Box component="strong" sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {segment.label}:
                          </Box>
                          {' '}
                          <Box component="span">{segment.value}</Box>
                        </React.Fragment>
                      ))}
                    </Typography>
                  </Box>
                ) : null}

              <NutritionalAnalysisActionsBar
                isExporting={false}
                showExport={false}
                isDeletingTemplate={isDeletingTemplate}
                onSaveAsTemplate={openSaveTemplateModal}
                saveAsTemplateDisabled={mealPlanTemplateDisabledReason !== null}
                saveAsTemplateDisabledReason={mealPlanTemplateDisabledReason ?? undefined}
                showSaveAsTemplate={canShowSaveAsTemplateFlow}
                showDeleteTemplate={isTemplateEditor && canDeleteTemplateFromRouteState && currentUserIdCandidates.length > 0}
                onDeleteTemplate={() => {
                  void handleDeleteTemplate();
                }}
                templatesSearchPlaceholder={effectiveTemplatesSearchPlaceholder}
                showTemplatesSearch={!isGenericOriginPlan}
                showGenericMealPlanImport={isGenericPlan}
                currentGenericPlanId={resolvedPlanId}
                genericMealPlanImportClientId={resolvedClientId}
                onGenericMealPlanImport={handleGenericMealPlanImport}
                onTemplatePick={handleTemplatePickImport}
                excludeImportedTemplateIds={importedTemplateIds}
                restrictionClient={clientDetails}
                onAddMeal={openAddMealSlotDialog}
                canManageMealSlots={canDeleteMealSlots && !isSavingMealSlot}
                showAddMeal={
                  !embedded
                  && !isAiAssistedGeneration
                  && !(isTemplateEditor && loadedTemplateKind === 'meal_slot')
                  && !templateInteractionDisabled
                  && !(isGenericOriginPlan && isFinalizedMealPlanStatus(planStatus))
                }
              />

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleMealCardDragStart}
                onDragEnd={(event) => {
                  void handleMealCardDragEnd(event);
                }}
                onDragCancel={() => {
                  setDraggingMealName(null);
                }}
              >
                <SortableContext
                  items={mealCards.map((meal) => mealSlotDragId(meal.name))}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={2.2}>
                    {mealCards.length === 0 ? (
                      <Paper
                        elevation={0}
                        sx={{
                          border: '1px solid #cbd5e1',
                          borderRadius: '16px',
                          bgcolor: '#f8fbfd',
                          px: { xs: 2, md: 3 },
                          py: { xs: 3, md: 4 },
                          textAlign: 'center',
                          color: '#667085',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.92rem',
                        }}
                      >
                        No meal slots added yet. Use Add Meal to create a slot, then import recipes or add foods.
                      </Paper>
                    ) : mealCards.map((meal) => {
                      const isExpanded = Boolean(expandedCards[meal.name]);
                      const rows = rowsByMeal[meal.name] || [];
                      return (
                        <SortableMealSlotWrapper
                          key={meal.name}
                          id={mealSlotDragId(meal.name)}
                          disabled={!canDragMealSlots}
                        >
                          {({ setNodeRef, attributes, listeners, style, isDragging }) => (
                            <NutritionalAnalysisMealCard
                              meal={meal}
                              rows={rows}
                              isExpanded={isExpanded}
                              publicTemplateViewer={isPublicTemplateViewerLayout}
                              interactionDisabled={templateInteractionDisabled}
                              canEditNotes={canEditMealPlanNotes}
                              isSavingNotes={isSavingMealPlanNotes}
                              selectedDayNumber={selectedDayNumber}
                              isDraftPlan={planStatus === 'draft'}
                              onSaveMealPlanNotes={handleSaveMealPlanNotes}
                              showOrBetweenRows={isGenericOriginPlan}
                              isAddingGenericOptionItem={addingGenericOptionMealName === meal.name}
                              genericOptionMutationDisabled={isGenericOriginPlan && isFinalizedMealPlanStatus(planStatus)}
                              showRowCheckboxes={isGenericOriginPlan}
                              getRowCheckboxChecked={getNutritionRowCheckboxChecked}
                              onRowCheckboxChange={handleNutritionRowCheckboxChange}
                              planStatus={canDeleteMealSlots ? 'draft' : effectivePlanStatus}
                              mealSlotDeletingName={mealSlotDeletingName}
                              isSavingMealSlot={isSavingMealSlot}
                              onToggleExpand={() => toggleMealCard(meal.name)}
                              onSaveMealSlotTemplate={() => openMealSlotTemplateModal(meal.name)}
                              saveAsTemplateDisabled={!slotHasRecipesByName[meal.name]}
                              saveAsTemplateDisabledReason="Add recipes to this meal slot to enable saving it as a template."
                              showSaveAsTemplate={canShowSaveAsTemplateFlow}
                              onEditMealSlot={() => openEditMealSlotDialog(meal.name)}
                              onDeleteMealSlot={() => {
                                void handleDeleteMealSlotRequest(meal);
                              }}
                              onOpenAddItem={(anchor, optionContext) => openAddItemModal(meal.name, undefined, anchor, optionContext)}
                              onImportRecipes={(optionContext) => openImportRecipesModal(meal.name, optionContext)}
                              findEmptyRowSearchAnchor={findEmptyRowSearchAnchor}
                              getRowSearchId={(row, index) =>
                                row.rowId ?? resolveRowId(meal.name, index, row.itemId, row.item)
                              }
                              getRowSearchResults={(rowSearchId) => rowItemSearchResultsById[rowSearchId] ?? []}
                              getRowSearchLoading={(rowSearchId) => Boolean(rowItemSearchLoadingById[rowSearchId])}
                              isRowAddingItem={isRowAddingItem}
                              isMealLevelAddingItem={isMealLevelAddingItem}
                              isRowDeletingItem={isRowDeletingItem}
                              hasActiveRowDeletion={hasActiveRowDeletion}
                              isImportingRecipes={importingRecipesMealName === meal.name}
                              getRowSearchOpen={(rowSearchId) => Boolean(rowItemSearchOpenById[rowSearchId])}
                              onRowItemTextChange={(mealName, index, rowSearchId, value) => {
                                updateRowField(mealName, index, 'item', value);
                                queueRowItemSuggestions(rowSearchId, value);
                                setRowItemSearchOpenById((prev) => ({ ...prev, [rowSearchId]: true }));
                              }}
                              onRowItemSelect={(mealName, index, rowSearchId, item) => {
                                const addingRowKey = buildAddingItemRowKey(mealName, rowSearchId);
                                handleRowItemSelect(mealName, index, rowSearchId, item);
                                if (item.type === 'ingredient') {
                                  void handleInlineIngredientSelect(mealName, item, {
                                    insideRecipe: true,
                                    addingRowKey,
                                  });
                                } else {
                                  void handleInlineFoodItemSelect(mealName, item, { addingRowKey });
                                }
                              }}
                              onCloseRowSearch={(rowSearchId) => {
                                setRowItemSearchOpenById((prev) => ({ ...prev, [rowSearchId]: false }));
                              }}
                              onFocusRowSearch={(mealName, index, row, rowSearchId, event) => {
                                setRowItemSearchOpenById((prev) => ({ ...prev, [rowSearchId]: false }));
                                openAddItemModal(
                                  mealName,
                                  {
                                    rowIndex: index,
                                    rowSearchId,
                                    type: row.itemType === 'ingredient' ? 'ingredient' : 'food',
                                  },
                                  event.currentTarget,
                                );
                              }}
                              onAdjustRowQty={adjustRowQty}
                              onUpdateRowField={updateRowField}
                              getRowQtyDraft={getRowQtyDraft}
                              onQtyDraftChange={handleQtyDraftChange}
                              isRowQuantitySyncing={isRowQuantitySyncing}
                              onDownloadRecipeFromRow={handleDownloadRecipePdf}
                              getRowDownloadLoading={(mealName, index, row) =>
                                downloadingRecipeRowKey === getRecipeDownloadRowKey(mealName, index, row)
                              }
                              onDeleteRow={handleDeleteRow}
                              onRecipeDetailsFromRow={(mealName, index, row) => {
                                const recipeIdCandidate = String(row.recipeId ?? '').trim();
                                if (!recipeIdCandidate) {
                                  showWarningToast('Recipe id is unavailable for this item.');
                                  return;
                                }
                                const itemIdCandidate = String(row.itemId ?? '').trim();
                                if (!itemIdCandidate) {
                                  showWarningToast('Item id is unavailable for this recipe.');
                                  return;
                                }
                                const currentMealMeta = mealMeta.find((entry) => entry.name === mealName);
                                setSelectedRecipeDetails({
                                  recipeId: recipeIdCandidate,
                                  itemId: itemIdCandidate,
                                  recipeName: row.item || 'Recipe',
                                  mealName,
                                  mealOrder: currentMealMeta?.sortOrder,
                                  source: row.source ?? 'spoonacular',
                                });
                              }}
                              onOpenIngredientModalFromRow={(mealName, index, rowSearchId, anchor) => {
                                openAddItemModal(mealName, { rowIndex: index, rowSearchId, type: 'ingredient' }, anchor);
                              }}
                              isExistingAIItemRow={isExistingAIItemRow}
                              canDragMealSlot={canDragMealSlots}
                              isDragging={isDragging || draggingMealName === meal.name}
                              setNodeRef={setNodeRef}
                              sortableStyle={style}
                              dragHandleAttributes={attributes}
                              dragHandleListeners={listeners}
                            />
                          )}
                        </SortableMealSlotWrapper>
                      );
                    })}
                  </Stack>
                </SortableContext>
                <DragOverlay dropAnimation={null}>
                  {draggingMealName ? (
                    <Paper
                      elevation={0}
                      sx={{
                        width: { xs: '100%', md: 880 },
                        maxWidth: '100%',
                        borderRadius: '8px',
                        border: '1px solid #20AEDC',
                        overflow: 'hidden',
                        boxShadow: '0 16px 28px rgba(16, 24, 40, 0.16)',
                        bgcolor: '#ffffff',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          minHeight: 44,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          background: 'linear-gradient(90deg, #32B8DE 0%, #20AEDC 100%)',
                          color: '#ffffff',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '20px',
                        }}
                      >
                        <Box component="span">{draggingMealName}</Box>
                        <Box component="span" sx={{ fontSize: '12px', fontWeight: 500, opacity: 0.95 }}>
                          Drag to reorder
                        </Box>
                      </Box>
                    </Paper>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {showManualFinalizeAction ? (
                <Box sx={{ mt: 2.2 }}>
                  {manualFinalizeValidation?.allMealSlotsFilled || isValidatingManualFinalize ? (
                    <ClinicalFinalizeIssuesPanel
                      tone={
                        isValidatingManualFinalize
                          ? 'checking'
                          : manualFinalizeValidation?.status === 'blocked'
                            ? 'blocked'
                            : (manualFinalizeValidation?.warnings?.length || 0) > 0
                              ? 'warning'
                              : 'success'
                      }
                      title={
                        isValidatingManualFinalize
                          ? 'Checking clinical hard blocks…'
                          : manualFinalizeValidation?.status === 'blocked'
                            ? `${countClinicalIssueGroups(manualFinalizeValidation.blockingIssues)} clinical hard-block group${countClinicalIssueGroups(manualFinalizeValidation.blockingIssues) === 1 ? '' : 's'} must be resolved`
                            : (manualFinalizeValidation?.warnings?.length || 0) > 0
                              ? `${countClinicalIssueGroups(manualFinalizeValidation.warnings)} clinical warning group${countClinicalIssueGroups(manualFinalizeValidation.warnings) === 1 ? '' : 's'} to review before finalizing`
                              : 'Clinical hard-block checks passed'
                      }
                      selectedDayNumber={selectedDayNumber}
                      visibleBlockingIssues={groupedManualFinalizeIssues.visibleBlockingIssues}
                      visibleWarnings={groupedManualFinalizeIssues.visibleWarnings}
                      otherDayBlockingIssues={groupedManualFinalizeIssues.otherDayBlockingIssues}
                      otherDayWarnings={groupedManualFinalizeIssues.otherDayWarnings}
                      otherDayNumbers={groupedManualFinalizeIssues.otherDayNumbers}
                    />
                  ) : null}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    data-demo-id="nutrition-finalise"
                    variant="contained"
                    startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      void handleSaveChanges();
                    }}
                    disabled={isInlineSaving || isAISaveSimulating || isValidatingManualFinalize}
                    sx={{
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                      height: '38px',
                      px: '14px',
                      borderRadius: '8px',
                      bgcolor: '#20AEDC',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#20AEDC',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {saveChangesLabel}
                  </Button>
                  </Box>
                </Box>
              ) : null}

              {!isTemplateEditor && !isGenericPlan && (previousDayNumber != null || nextDayNumber != null) ? (
                <Box sx={{ mt: 2.2, display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                  {previousDayNumber != null ? (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
                      onClick={() => {
                        setSelectedDayNumber(previousDayNumber);
                        contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '20px',
                        height: '38px',
                        px: '14px',
                        borderRadius: '8px',
                        color: '#20AEDC',
                        borderColor: '#20AEDC',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#1899C3',
                          bgcolor: 'rgba(32, 174, 220, 0.06)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Previous Day{previousDayLabel ? ` (${previousDayLabel})` : ''}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  {nextDayNumber != null ? (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
                      onClick={() => {
                        setSelectedDayNumber(nextDayNumber);
                        contentScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: '20px',
                        height: '38px',
                        px: '14px',
                        borderRadius: '8px',
                        bgcolor: '#20AEDC',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#1899C3',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Next Day{nextDayLabel ? ` (${nextDayLabel})` : ''}
                    </Button>
                  ) : (
                    <Box />
                  )}
                </Box>
              ) : null}
            </Box>

            {showAnalysisSidebar ? (
              <NutritionalAnalysisSidebar
                totalIntake={totalIntake}
                macroTargets={sidebarMacroTargets}
                calorieProgress={calorieTargetPercent}
                macroRows={macroRows}
                mealBreakdown={mealBreakdown}
                onAnalyseClick={handleOpenManualAnalysisDrawer}
                showAnalyseButton={!isGenericPlan}
                showTargets={!isGenericPlan}
                clinicalInsights={sidebarClinicalInsights}
              />
            ) : null}
            </Box>
          </Box>
        )}
      </Paper>

      <NutritionalAnalysisOverlays
        isAddItemModalOpen={isAddItemModalOpen}
        addItemAnchorEl={addItemAnchorEl}
        closeAddItemModal={closeAddItemModal}
        addItemMealName={addItemMealName}
        isAddItemSearching={isAddItemSearching}
        isAddItemAdding={isAddItemPopoverAdding}
        addItemAddingOption={addItemPopoverAddingOption}
        addItemSearchValue={addItemSearchValue}
        setAddItemSearchValue={setAddItemSearchValue}
        addItemSearchInputRef={addItemSearchInputRef}
        addItemOptionNames={addItemOptionNames}
        addItemSearchResults={enrichedAddItemSearchResults}
        addItemSearchFilters={addItemSearchFilters}
        addItemCurrentAllergies={clientAllergiesForSearch}
        addItemCurrentIntolerances={addItemCurrentIntolerances}
        handleAddItemFromModal={handleAddItemFromModal}
        onCreateCustomRecipe={
          resolvedClientId
          && resolvedClientId !== 'template-preview'
          && resolvedPlanId
            ? handleCreateCustomRecipeFromAddItem
            : undefined
        }
        isImportRecipesOpen={isImportRecipesOpen}
        closeImportRecipesModal={closeImportRecipesModal}
        importRecipesMealName={importRecipesMealName}
        hiddenImportedRecipeIds={hiddenImportedRecipeIds}
        excludedAllergies={clientAllergiesForSearch}
        handleImportRecipes={handleImportRecipes}
        editMealDialogOpen={editMealDialogOpen}
        mealSlotDialogMode={mealSlotDialogMode}
        isSavingMealSlot={isSavingMealSlot}
        editMealName={editMealName}
        editMealType={editMealType}
        canEditMealType={mealTypeOptions.length > 0}
        mealTypeOptions={mealTypeOptions}
        editMealTime={editMealTime}
        editMealTargetKcal={editMealTargetKcal}
        setEditMealName={setEditMealName}
        setEditMealType={setEditMealType}
        setEditMealTime={setEditMealTime}
        setEditMealTargetKcal={setEditMealTargetKcal}
        closeEditMealSlotDialog={closeEditMealSlotDialog}
        handleSaveMealSlotEdit={handleSaveMealSlotEdit}
        selectedRecipeDetails={selectedRecipeDetails}
        setSelectedRecipeDetails={setSelectedRecipeDetails}
        resolvedPlanId={resolvedPlanId}
        planStatus={effectivePlanStatus}
        selectedDayNumber={selectedDayNumber}
        resolveMealApiName={resolveMealApiName}
        loadDayData={loadDayData}
        isSaveTemplateOpen={isSaveTemplateOpen}
        closeSaveTemplateModal={closeSaveTemplateModal}
        saveTemplateKind={saveTemplateKind}
        dayLabel={saveTemplateModalLabel}
        mealCount={saveTemplateMealCount}
        totalCalories={saveTemplateTotalCalories}
        saveTemplateSummaryDetail={saveTemplateSummaryDetail}
        handleSaveTemplate={handleSaveTemplate}
        deleteMealSlotConfirmOpen={deleteMealSlotConfirmOpen}
        setDeleteMealSlotConfirmOpen={setDeleteMealSlotConfirmOpen}
        pendingDeleteMealSlotName={pendingDeleteMealSlotName}
        mealSlotDeletingName={mealSlotDeletingName}
        handleDeleteMealSlotConfirmSubmit={handleDeleteMealSlotConfirmSubmit}
        deleteTemplateConfirmOpen={deleteTemplateConfirmOpen}
        setDeleteTemplateConfirmOpen={setDeleteTemplateConfirmOpen}
        isDeletingTemplate={isDeletingTemplate}
        handleDeleteTemplateConfirmSubmit={handleDeleteTemplateConfirmSubmit}
        manualAnalysisDrawerOpen={manualAnalysisDrawerOpen}
        setManualAnalysisDrawerOpen={setManualAnalysisDrawerOpen}
        manualAnalysisSnapshot={manualAnalysisSnapshot}
        targetDailyCalories={sidebarMacroTargets.calories || 2000}
        onViewDetailedAnalysis={shouldUseClientContext ? handleViewDetailedAnalysis : undefined}
      />
      <Dialog
        open={finalizeDateConflicts.length > 0}
        onClose={() => setFinalizeDateConflicts([])}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Overwrite existing meal plans?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.92rem', color: '#475467' }}>
            The selected dates are already used. Continuing will mark these meal plans inactive:
          </Typography>
          <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.5 }}>
            {finalizeDateConflicts.map((item) => (
              <li key={item.id}>
                <Typography component="span" sx={{ fontSize: '0.86rem', color: '#344054' }}>
                  {item.name} ({formatConflictDateRange(item)})
                </Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setFinalizeDateConflicts([])} disabled={isInlineSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFinalizeDateConflicts([]);
              void handleSaveChanges(true);
            }}
            disabled={isInlineSaving}
          >
            Overwrite and finalize
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NutritionalAnalysisWorkspace;
